import React2, { useState, useCallback, useRef, Children, isValidElement, useEffect } from 'react';
import { styled, css, keyframes, createGlobalStyle } from 'styled-components';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useCalendarState } from '@react-stately/calendar';
import { useCalendar, useCalendarGrid } from '@react-aria/calendar';
import { useLocale } from '@react-aria/i18n';
import { Button, Modal, RadioGroup, Radio, Label, Dialog, DialogTrigger } from 'react-aria-components';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/foundations/tokens/breakpoints.ts
var BREAKPOINTS = {
  s: "375px",
  m: "768px",
  l: "1200px",
  xl: "1536px"
};
var StyledFlex = styled.div`
  display: flex;

  ${({ $direction }) => $direction && `flex-direction: ${$direction};`}
  ${({ $alignContent }) => $alignContent && `align-content: ${$alignContent};`}
  ${({ $alignItems }) => $alignItems && `align-items: ${$alignItems};`}
  ${({ $height }) => $height && `height: ${$height};`}
  ${({ $gap }) => $gap !== void 0 && `gap: var(--grid-${$gap.toString()});`}
  ${({ $justifyContent }) => $justifyContent && `justify-content: ${$justifyContent};`}
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $wrap }) => $wrap && `flex-wrap: ${$wrap};`}
  ${({ $flex }) => $flex && `flex: ${$flex};`}

  ${({ $breakConfig }) => $breakConfig && `
      flex-direction: ${$breakConfig.maxViewportDirection};

      @media screen and (max-width: ${BREAKPOINTS[$breakConfig.breakAt.breakPoint]}) {
        flex-direction: ${$breakConfig.breakAt.direction};
        ${$breakConfig.breakAt.gap === 0 ? `gap: 0;` : ""}
        ${$breakConfig.breakAt.gap ? `gap: var(--grid-${$breakConfig.breakAt.gap.toString()});` : ""}
        ${$breakConfig.breakAt.alignItems ? `align-items: ${$breakConfig.breakAt.alignItems};` : ""}
        ${$breakConfig.breakAt.alignContent ? `align-content: ${$breakConfig.breakAt.alignContent};` : ""}
        ${$breakConfig.breakAt.justifyContent ? `justify-content: ${$breakConfig.breakAt.justifyContent};` : ""}
      }
  `}
`;
var DSFlex = ({
  direction = "row",
  breakConfig,
  children,
  alignContent,
  alignItems,
  gap,
  height,
  justifyContent,
  width,
  wrap,
  flex,
  testId
}) => {
  return /* @__PURE__ */ jsx(
    StyledFlex,
    __spreadProps(__spreadValues({
      $direction: direction,
      $breakConfig: breakConfig,
      $alignContent: alignContent,
      $alignItems: alignItems,
      $gap: gap,
      $height: height,
      $justifyContent: justifyContent,
      $width: width,
      $wrap: wrap,
      $flex: flex
    }, testId ? { "data-testid": testId } : {}), {
      children
    })
  );
};
var StyledSpacer = styled.div`
  flex: none; // ensures if parent is flexbox, it doesn't grow/shrink

  &.horizontal {
    width: ${(props) => `var(--grid-${String(props.$size)});`};
  }

  &.vertical {
    width: 100%;
    height: ${(props) => `var(--grid-${String(props.$size)});`};
  }

  ${(props) => props.$breakpoints && Object.entries(props.$breakpoints).map(
  ([breakpoint, size]) => css`
        @media (max-width: ${BREAKPOINTS[breakpoint]}) {
          &.horizontal {
            width: var(--grid-${size});
          }

          &.vertical {
            width: 100%;
            height: var(--grid-${size});
          }
        }
      `
)}
`;
function DSSpacer({ size, direction, breakAt, testId }) {
  return /* @__PURE__ */ jsx(
    StyledSpacer,
    __spreadValues({
      $breakpoints: breakAt,
      $size: size,
      className: direction,
      "aria-hidden": true
    }, testId ? { "data-testid": testId } : {})
  );
}
var typographyStyles = {
  "fluid-display-1100": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-display-1100);
    letter-spacing: calc(var(--font-size-fluid-heading-1100) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-display-1100);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-display-1000": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-display-1000);
    letter-spacing: calc(var(--font-size-fluid-heading-1000) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-display-1000);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-heading-900": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-heading-900);
    letter-spacing: calc(var(--font-size-fluid-heading-900) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-heading-900);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-heading-800": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-heading-800);
    letter-spacing: calc(var(--font-size-fluid-heading-800) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-heading-800);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-heading-700": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-heading-700);
    letter-spacing: calc(var(--font-size-fluid-heading-700) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-heading-700);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-heading-600": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-heading-600);
    letter-spacing: calc(var(--font-size-fluid-heading-600) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-heading-600);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-heading-500": () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-size: var(--font-size-fluid-heading-500);
    letter-spacing: calc(var(--font-size-fluid-heading-500) * var(--letter-spacing-ratio-heading));
    line-height: var(--line-height-fluid-heading-500);
    font-weight: var(--font-serif-weight);

    em {
      font-style: normal;
      font-family: var(--font-serif-italic), var(--font-fallback-serif);
      font-weight: var(--font-serif-italic-weight);
    }
  `,
  "fluid-heading-500alt": () => css`
    font-size: var(--font-size-fluid-heading-500);
    line-height: var(--line-height-fluid-heading-500-alt);
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-regular-weight);

    b,
    strong {
      font-weight: var(--font-sans-serif-semibold-weight);
    }
  `,
  "fluid-paragraph-400": () => css`
    font-size: var(--font-size-fluid-paragraph-400);
    line-height: var(--line-height-fluid-paragraph-400);
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-regular-weight);

    b,
    strong {
      font-weight: var(--font-sans-serif-semibold-weight);
    }
  `,
  "fluid-paragraph-300": () => css`
    font-size: var(--font-size-fluid-paragraph-300);
    line-height: var(--line-height-fluid-paragraph-300);
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-regular-weight);

    b,
    strong {
      font-weight: var(--font-sans-serif-semibold-weight);
    }
  `,
  "fluid-paragraph-200": () => css`
    font-size: var(--font-size-fluid-paragraph-200);
    line-height: var(--line-height-fluid-paragraph-200);
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-regular-weight);

    b,
    strong {
      font-weight: var(--font-sans-serif-semibold-weight);
    }
  `,
  "fluid-eyebrow-100": () => css`
    font-size: var(--font-size-fluid-label-100);
    line-height: var(--line-height-fluid-label-100);
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    letter-spacing: calc(var(--font-size-fluid-label-100) * var(--letter-spacing-ratio-eyebrow));
    font-weight: var(--font-sans-serif-regular-weight);
    text-transform: uppercase;
  `,
  "fluid-label-100": () => css`
    font-size: var(--font-size-fluid-label-100);
    line-height: var(--line-height-fluid-label-100);
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-regular-weight);
  `
};
var fontFamilies = {
  book: () => css`
    font-family: var(--font-serif), var(--font-fallback-serif);
    font-weight: var(--font-serif-weight);
  `,
  bookItalic: () => css`
    font-family: var(--font-serif-italic), var(--font-fallback-serif);
    font-weight: var(--font-serif-italic-weight);
  `,
  light: () => css`
    font-family: var(--font-sans-serif-light), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-light-weight);
  `,
  regular: () => css`
    font-family: var(--font-sans-serif-regular), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-regular-weight);
  `,
  semiBold: () => css`
    font-family: var(--font-sans-serif-semibold), var(--font-fallback-sans-serif);
    font-weight: var(--font-sans-serif-semibold-weight);
  `
};
var StyledTextElement = styled.div`
  ${({ $variant }) => typographyStyles[$variant]()}
  color: ${({ $color }) => `var(--color-text-${$color})`};
  text-align: ${({ $align }) => $align};
  text-decoration: ${({ $textDecoration }) => $textDecoration};
  ${({ $weight }) => $weight ? fontFamilies[$weight]() : ""}
  margin: 0;
  a {
    font-weight: var(--font-sans-serif-semibold-weight);
    font-family: var(--font-sans-serif-semibold),var(--font-fallback-sans-serif);
    text-decoration: none;

    ${({ $color }) => $color.includes("inverse") ? `color: var(--color-text-link-inverse);` : `color: var(--color-text-link);`}
    :hover {
    ${({ $color }) => $color.includes("inverse") ? `color: var(--color-text-link-inverse);` : `color: var(--color-text-link-hover);`}
  }

  ${({ $breakConfig }) => $breakConfig && css`
      @media (max-width: ${BREAKPOINTS[$breakConfig.breakPoint]}) {
        ${$breakConfig.variant ? typographyStyles[$breakConfig.variant]() : ""}
        ${$breakConfig.align ? `text-align: ${$breakConfig.align};` : ""}
      }
    `}
`;
var DSText = (_a) => {
  var _b = _a, {
    as = "p",
    color = "primary",
    align = "inherit",
    variant,
    weight,
    textDecoration,
    children,
    testId,
    breakAt
  } = _b, props = __objRest(_b, [
    "as",
    "color",
    "align",
    "variant",
    "weight",
    "textDecoration",
    "children",
    "testId",
    "breakAt"
  ]);
  return /* @__PURE__ */ jsx(
    StyledTextElement,
    __spreadProps(__spreadValues(__spreadProps(__spreadValues({
      as,
      $variant: variant,
      $color: color,
      $weight: weight,
      $align: align,
      $textDecoration: textDecoration
    }, testId ? { "data-testid": testId } : {}), {
      $breakConfig: breakAt
    }), props), {
      children
    })
  );
};
var DSTextStrong = (_a) => {
  var _b = _a, {
    as = "p",
    color = "primary",
    align = "inherit",
    variant,
    textDecoration,
    children,
    breakAt,
    testId
  } = _b, props = __objRest(_b, [
    "as",
    "color",
    "align",
    "variant",
    "textDecoration",
    "children",
    "breakAt",
    "testId"
  ]);
  return /* @__PURE__ */ jsx(
    StyledTextElement,
    __spreadProps(__spreadValues(__spreadValues({
      as,
      $variant: variant,
      $color: color,
      $weight: "semiBold",
      $align: align,
      $textDecoration: textDecoration,
      $breakConfig: breakAt
    }, testId ? { "data-testid": testId } : {}), props), {
      children: /* @__PURE__ */ jsx("strong", { children })
    })
  );
};
var DSTextEm = (_a) => {
  var _b = _a, {
    as = "p",
    color = "primary",
    align = "inherit",
    variant,
    weight = "light",
    textDecoration,
    children,
    breakAt,
    testId
  } = _b, props = __objRest(_b, [
    "as",
    "color",
    "align",
    "variant",
    "weight",
    "textDecoration",
    "children",
    "breakAt",
    "testId"
  ]);
  return /* @__PURE__ */ jsx(
    StyledTextElement,
    __spreadProps(__spreadValues(__spreadValues({
      as,
      $variant: variant,
      $color: color,
      $weight: weight,
      $align: align,
      $textDecoration: textDecoration,
      $breakConfig: breakAt
    }, testId ? { "data-testid": testId } : {}), props), {
      children: /* @__PURE__ */ jsx("em", { children })
    })
  );
};
var StyledHorizontalDivider = styled.hr`
  border: none;
  border-top: ${({ $size, $variant }) => `${($size != null ? $size : 1).toString()}px solid var(--color-divider-${$variant != null ? $variant : "primary"});`};
  margin: 0;
  width: ${({ $width }) => $width ? `${String($width)}px` : "100%"};
  ${({ $width }) => $width ? "margin-left: auto; margin-right: auto;" : ""}
`;
var StyledVerticalDivider = styled.div`
  border-left: ${({ $variant }) => `1px solid var(--color-divider-${$variant != null ? $variant : "primary"});`};
  height: ${({ $height }) => $height ? `${String($height)}px` : "auto"};
  width: 0;
`;
function DSDivider({
  content,
  direction = "horizontal",
  spacing,
  height,
  variant,
  width,
  testId
}) {
  if (direction === "vertical") {
    return /* @__PURE__ */ jsxs(DSFlex, __spreadProps(__spreadValues({ direction: "row" }, testId ? { "data-testid": testId } : {}), { children: [
      spacing ? /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: spacing }) : null,
      /* @__PURE__ */ jsx(StyledVerticalDivider, { $size: height, $variant: variant, $height: height }),
      spacing ? /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: spacing }) : null
    ] }));
  }
  if (!content) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      spacing ? /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: spacing }) : null,
      /* @__PURE__ */ jsx(
        StyledHorizontalDivider,
        __spreadValues({
          $size: height,
          $variant: variant,
          $width: width
        }, testId ? { "data-testid": testId } : {})
      ),
      spacing ? /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: spacing }) : null
    ] });
  }
  return /* @__PURE__ */ jsxs(DSFlex, __spreadProps(__spreadValues({ alignItems: "center", direction: "row" }, testId ? { "data-testid": testId } : {}), { children: [
    spacing ? /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: spacing }) : null,
    /* @__PURE__ */ jsx(StyledHorizontalDivider, { $variant: variant, $width: width }),
    /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: 12 }),
    /* @__PURE__ */ jsx(DSText, { variant: "fluid-label-100", children: content }),
    /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: 12 }),
    /* @__PURE__ */ jsx(StyledHorizontalDivider, { $variant: variant }),
    spacing ? /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: spacing }) : null
  ] }));
}
var ChevronDown = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 16 16", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M2.33594 6.05664L7.78066 11.5014C7.89782 11.6185 8.08777 11.6185 8.20492 11.5014L13.6496 6.05664",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var ChevronUp = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 16 16", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M13.6641 9.94336L8.21934 4.49864C8.10218 4.38148 7.91223 4.38148 7.79508 4.49864L2.35035 9.94336",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var ChevronRight = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    "aria-hidden": ariaHidden,
    fill: "none",
    height: "16",
    style,
    viewBox: "0 0 16 16",
    width: "16",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.64645 1.64645C4.84171 1.45118 5.15829 1.45118 5.35355 1.64645L11.3536 7.64645C11.5488 7.84171 11.5488 8.15829 11.3536 8.35355L5.35355 14.3536C5.15829 14.5488 4.84171 14.5488 4.64645 14.3536C4.45118 14.1583 4.45118 13.8417 4.64645 13.6464L10.2929 8L4.64645 2.35355C4.45118 2.15829 4.45118 1.84171 4.64645 1.64645Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);
var ChevronLeft = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    "aria-hidden": ariaHidden,
    fill: "none",
    height: "16",
    style,
    viewBox: "0 0 16 16",
    width: "16",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.3536 1.64645C11.1583 1.45118 10.8417 1.45118 10.6464 1.64645L4.64645 7.64645C4.45118 7.84171 4.45118 8.15829 4.64645 8.35355L10.6464 14.3536C10.8417 14.5488 11.1583 14.5488 11.3536 14.3536C11.5488 14.1583 11.5488 13.8417 11.3536 13.6464L5.70711 8L11.3536 2.35355C11.5488 2.15829 11.5488 1.84171 11.3536 1.64645Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);
var CheckboxTick = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 14 12", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M1.44995 6.80005L4.901 10.2511L12.8499 1.5",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2"
  }
) });
var Close = ({ style, ariaHidden }) => {
  var _a;
  return /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M18.3636 18.3638L5.63574 5.63599M5.63574 18.3638L18.3636 5.63599",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: (_a = style.strokeWidth) != null ? _a : 1.5
    }
  ) });
};
var Delivery = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M3 18.375H1.2C0.951472 18.375 0.75 18.1735 0.75 17.925V3.825C0.75 3.57647 0.951472 3.375 1.2 3.375H16.05C16.2985 3.375 16.5 3.57647 16.5 3.825V15.75M15 18.375H9M15 18.375C15 19.8247 16.1753 21 17.625 21C19.0747 21 20.25 19.8247 20.25 18.375M15 18.375C15 16.9253 16.1753 15.75 17.625 15.75C19.0747 15.75 20.25 16.9253 20.25 18.375M16.5 9.375H20.0092C20.1596 9.375 20.3001 9.4502 20.3836 9.57538L23.1744 13.7616C23.2237 13.8356 23.25 13.9224 23.25 14.0112V17.925C23.25 18.1735 23.0485 18.375 22.8 18.375H20.25M8.625 18.375C8.625 19.8247 7.44975 21 6 21C4.55025 21 3.375 19.8247 3.375 18.375C3.375 16.9253 4.55025 15.75 6 15.75C7.44975 15.75 8.625 16.9253 8.625 18.375Z",
    stroke: "currentColor",
    strokeLinecap: "round"
  }
) });
var Email = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    "aria-hidden": ariaHidden,
    fill: "none",
    style,
    xmlns: "http://www.w3.org/2000/svg",
    width: "20",
    height: "20",
    viewBox: "0 0 20 20",
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M3.75 6.25L10 11.875L16.25 6.25M7.91667 10L3.75 13.75M16.25 13.75L12.0833 10M19.0625 15.625V4.375C19.0625 3.68464 18.5029 3.125 17.8125 3.125H2.1875C1.49714 3.125 0.9375 3.68464 0.9375 4.375V15.625C0.9375 16.3154 1.49714 16.875 2.1875 16.875H17.8125C18.5029 16.875 19.0625 16.3154 19.0625 15.625Z",
        stroke: "currentColor",
        strokeMiterlimit: "10",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var Minus = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 17 1", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    clipRule: "evenodd",
    d: "M0 0.5C-6.03528e-09 0.223858 0.223858 3.55726e-07 0.5 3.49691e-07L16.5 0C16.7761 -6.03528e-09 17 0.223858 17 0.5C17 0.776142 16.7761 1 16.5 1L0.5 1C0.223858 1 6.03528e-09 0.776143 0 0.5Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });
var Plus = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 17 18", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    clipRule: "evenodd",
    d: "M7.91797 17C7.91797 17.2761 8.14183 17.5 8.41797 17.5C8.69411 17.5 8.91797 17.2761 8.91797 17V9.58008H16.5C16.7761 9.58008 17 9.35622 17 9.08008C17 8.80394 16.7761 8.58008 16.5 8.58008H8.91797V1C8.91797 0.723858 8.69411 0.5 8.41797 0.5C8.14183 0.5 7.91797 0.723858 7.91797 1V8.58008H0.5C0.223858 8.58008 0 8.80394 0 9.08008C0 9.35622 0.223858 9.58008 0.5 9.58008H7.91797V17Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });
var RadioSelected = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "11.5", stroke: "currentColor" }),
  /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", fill: "currentColor", r: "8" })
] });
var RadioUnselected = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "11.5", stroke: "currentColor" }) });
var TickInCircleNoFill = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 33 32", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx("rect", { height: "31", rx: "15.5", stroke: "currentColor", width: "31", x: "1", y: "0.5" }),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M9.42773 17.2641L13.7444 21.5807L23.2025 11.1191",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
] });
var TickInStar = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M5.71708 21.2124H7.89374C8.09404 21.2124 8.24093 21.2658 8.38781 21.4127L9.93686 22.9484C11.339 24.3639 12.661 24.3505 14.0632 22.9484L15.6122 21.4127C15.7591 21.2658 15.906 21.2124 16.1196 21.2124H18.2829C20.2593 21.2124 21.2074 20.2776 21.2074 18.2879V16.1113C21.2074 15.911 21.2742 15.7641 21.421 15.6172L22.9434 14.0681C24.3589 12.666 24.3455 11.344 22.9434 9.92849L21.421 8.39282C21.2742 8.24593 21.2074 8.08567 21.2074 7.88537V5.72208C21.2074 3.74572 20.2726 2.78426 18.2829 2.78426H16.1196C15.906 2.78426 15.7591 2.73084 15.6122 2.58396L14.0632 1.06161C12.661 -0.353868 11.339 -0.353868 9.93686 1.06161L8.38781 2.58396C8.24093 2.73084 8.09404 2.78426 7.89374 2.78426H5.71708C3.72737 2.78426 2.7926 3.719 2.7926 5.72208V7.88537C2.7926 8.08567 2.73919 8.24593 2.5923 8.39282L1.05663 9.92849C-0.358878 11.344 -0.345524 12.666 1.05663 14.0681L2.5923 15.6172C2.73919 15.7641 2.7926 15.911 2.7926 16.1113V18.2879C2.7926 20.2643 3.72737 21.2124 5.71708 21.2124Z",
      fill: "currentColor"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M 8.0000002,12.158069 10.507,14.665069 16,8.5892766",
      stroke: "#2d3246",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.5"
    }
  )
] });
var VolumeMute = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx("path", { d: "M19.5 20.25L3 3.75", stroke: "currentColor", strokeLinecap: "round", strokeMiterlimit: "10", strokeWidth: "1.5" }),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M11.4061 4.63401C11.219 4.53306 11.007 4.48748 10.7949 4.50259C10.5828 4.51769 10.3794 4.59287 10.2084 4.71932L10.1775 4.74323L8.84063 5.83776C8.80007 5.87094 8.76691 5.91225 8.7433 5.95903C8.71969 6.00581 8.70615 6.05702 8.70354 6.10936C8.70093 6.16169 8.70933 6.214 8.72817 6.26289C8.74702 6.31178 8.77591 6.35618 8.81297 6.39323L11.3597 8.93995C11.4121 8.99245 11.479 9.02821 11.5518 9.04271C11.6245 9.0572 11.7 9.04978 11.7685 9.02137C11.8371 8.99296 11.8957 8.94485 11.9369 8.88313C11.9781 8.82141 12.0001 8.74885 12 8.67464V5.65167C12.0021 5.44422 11.948 5.24008 11.8434 5.06091C11.7389 4.88173 11.5877 4.73421 11.4061 4.63401ZM11.7811 15.7167L4.53234 8.46979C4.39176 8.32912 4.20106 8.25004 4.00219 8.24995H2.625C2.32663 8.24995 2.04048 8.36847 1.8295 8.57945C1.61853 8.79043 1.5 9.07658 1.5 9.37495V14.6249C1.5 14.9233 1.61853 15.2095 1.8295 15.4204C2.04048 15.6314 2.32663 15.7499 2.625 15.7499H5.895L10.2075 19.2801C10.3806 19.4086 10.5871 19.4844 10.8022 19.4984C11.0172 19.5125 11.2318 19.4642 11.4202 19.3593C11.5981 19.2579 11.7456 19.1108 11.8476 18.9332C11.9495 18.7556 12.0021 18.5539 12 18.3492V16.2464C12 16.0478 11.9213 15.8573 11.7811 15.7167ZM16.5 11.9999C16.5 10.8487 16.2277 9.75604 15.668 8.65917C15.6236 8.57076 15.5622 8.492 15.4872 8.42743C15.4123 8.36287 15.3253 8.31376 15.2313 8.28295C15.1373 8.25213 15.0381 8.24022 14.9395 8.24789C14.8409 8.25556 14.7448 8.28267 14.6567 8.32765C14.5686 8.37263 14.4902 8.43459 14.4262 8.50997C14.3621 8.58535 14.3136 8.67266 14.2834 8.76687C14.2532 8.86107 14.242 8.96032 14.2503 9.05889C14.2587 9.15746 14.2865 9.2534 14.332 9.3412C14.7816 10.2215 15 11.0915 15 11.9999C15 12.1249 14.9952 12.252 14.9855 12.381C14.9815 12.4348 14.9891 12.4888 15.0079 12.5394C15.0266 12.59 15.0561 12.6359 15.0942 12.674L15.7673 13.3471C15.816 13.3959 15.8771 13.4303 15.9439 13.4466C16.0108 13.4629 16.0809 13.4605 16.1465 13.4396C16.2121 13.4188 16.2707 13.3802 16.3158 13.3283C16.361 13.2763 16.391 13.213 16.4025 13.1451C16.4671 12.7668 16.4997 12.3837 16.5 11.9999ZM19.5 11.9999C19.5 9.60088 18.8869 8.0676 17.8978 6.37214C17.7962 6.2031 17.6322 6.08087 17.4411 6.03192C17.2501 5.98296 17.0475 6.01122 16.8771 6.11057C16.7068 6.20993 16.5824 6.37238 16.5309 6.56276C16.4795 6.75313 16.5051 6.95612 16.6022 7.12776C17.4877 8.64557 18 9.92948 18 11.9999C18 13.117 17.8458 14.0099 17.5608 14.8429C17.5379 14.9093 17.5341 14.9808 17.5498 15.0493C17.5656 15.1177 17.6002 15.1804 17.6498 15.2301L18.2344 15.8156C18.2786 15.8598 18.3331 15.8923 18.3931 15.9101C18.453 15.9279 18.5165 15.9304 18.5776 15.9174C18.6388 15.9044 18.6957 15.8763 18.7433 15.8357C18.7908 15.795 18.8275 15.7432 18.8498 15.6848C19.2656 14.6137 19.5 13.4653 19.5 11.9999Z",
      fill: "currentColor"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M22.501 12.0001C22.501 8.51961 21.5546 6.32304 20.1334 4.09648C20.0263 3.92865 19.857 3.81021 19.6626 3.76722C19.4683 3.72424 19.2648 3.76022 19.097 3.86726C18.9291 3.9743 18.8107 4.14363 18.7677 4.33799C18.7247 4.53236 18.7607 4.73584 18.8677 4.90367C20.1488 6.90851 21.001 8.88289 21.001 12.0001C21.001 14.1652 20.6059 15.7627 19.9496 17.1812C19.9175 17.2507 19.9074 17.3284 19.9207 17.4038C19.934 17.4792 19.9701 17.5487 20.0242 17.603L20.5829 18.1618C20.6252 18.2047 20.6771 18.2369 20.7344 18.2555C20.7917 18.2741 20.8526 18.2786 20.912 18.2687C20.9715 18.2587 21.0276 18.2346 21.0757 18.1984C21.1239 18.1622 21.1625 18.1149 21.1885 18.0605C22.1073 16.1673 22.501 14.2969 22.501 12.0001Z",
      fill: "currentColor"
    }
  )
] });
var InfoCircle = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 16 16", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15ZM8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z",
      fill: "#2D3246"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M8.9307 6.58789L6.63969 6.875L6.55766 7.25586L7.00883 7.33789C7.3018 7.4082 7.36039 7.51367 7.29594 7.80664L6.55766 11.2754C6.3643 12.1719 6.66313 12.5938 7.36625 12.5938C7.91117 12.5938 8.54398 12.3418 8.83109 11.9961L8.91898 11.5801C8.71977 11.7559 8.4268 11.8262 8.23344 11.8262C7.95805 11.8262 7.85844 11.6328 7.92875 11.293L8.9307 6.58789Z",
      fill: "#2D3246"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M9 4.5C9 5.05228 8.55229 5.5 8 5.5C7.44772 5.5 7 5.05228 7 4.5C7 3.94772 7.44772 3.5 8 3.5C8.55229 3.5 9 3.94772 9 4.5Z",
      fill: "#2D3246"
    }
  )
] });
var HandWaveLines = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 16 17", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M3.39296 6.38988C3.41102 6.41563 3.4281 6.44238 3.44412 6.47014L5.74767 10.4608L3.39296 6.38988ZM3.39296 6.38988C3.07852 5.94148 2.46788 5.80004 1.98459 6.07906C1.47357 6.3741 1.29847 7.02755 1.59352 7.53859L4.39557 12.3927C5.87021 14.9469 9.1362 15.822 11.6903 14.3474C14.2445 12.8728 15.1196 9.60677 13.645 7.05261L11.7379 3.74874C11.4429 3.23771 10.7895 3.06262 10.2785 3.35766C9.76744 3.6527 9.59231 4.30615 9.88736 4.81719L11.0454 6.8238L11.0215 6.84255C9.67119 7.90246 9.3058 9.794 10.164 11.2806M3.39296 6.38988L2.30707 4.50906C2.01202 3.99802 2.18712 3.34458 2.69815 3.04953C3.18143 2.77051 3.79208 2.91195 4.10652 3.36035M3.39296 6.38988L4.17497 7.7444M4.10652 3.36035C4.12457 3.3861 4.14165 3.41285 4.15768 3.44061L6.02557 6.67594L4.10652 3.36035ZM4.10652 3.36035L5.45656 5.69868M4.10652 3.36035L3.47675 2.26957C3.18171 1.75854 3.3568 1.10509 3.86783 0.810042C4.37886 0.514998 5.03231 0.690091 5.32736 1.20112L7.30716 4.63024M9.85836 4.76702L7.79008 1.18461C7.49508 0.67358 6.84162 0.498487 6.33059 0.793531C5.81956 1.08858 5.64447 1.74202 5.93951 2.25306L7.87616 5.6075M14.034 4.59459L12.9425 2.70007C12.6259 2.15169 12.103 1.79672 11.5283 1.67664M3.14488 13.3583L3.39866 13.7979C3.81145 14.5129 4.33277 15.1262 4.9298 15.6289",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var Phone = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M19.5007 5.25H4.50067M4.50067 18H19.5007M5.99936 1.5C9.64825 1.50002 14.3529 1.50008 18.0019 1.5C18.8303 1.49998 19.5007 2.17157 19.5007 3.00001V21C19.5007 21.8284 18.8291 22.5 18.0007 22.5C14.0006 22.5 10.0006 22.5 6.00058 22.5C5.17227 22.5 4.50074 21.8286 4.5006 21.0003C4.49961 15.0001 4.5 8.99999 4.50062 2.99983C4.50071 2.17147 5.17099 1.5 5.99936 1.5Z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var Community = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M8.8572 17.9265C7.66422 17.9265 6.53201 17.6678 5.51221 17.2044C5.34432 17.128 5.17946 17.0461 5.01789 16.959C4.93236 16.9129 4.83258 16.8991 4.73817 16.9227L1.24975 17.7963C0.966577 17.8673 0.712935 17.6062 0.795109 17.3284L1.77899 14.0023C1.80781 13.9049 1.79504 13.8002 1.74604 13.711C1.66391 13.5616 1.58637 13.4093 1.51359 13.2542C1.02387 12.2115 0.75 11.0478 0.75 9.81923C0.75 5.34175 4.38086 1.71204 8.8572 1.71204C13.3347 1.71204 16.9644 5.34175 16.9644 9.81923M8.8572 17.9265C13.3347 17.9265 16.9644 14.2967 16.9644 9.81923M8.8572 17.9265C8.54129 17.9265 8.22969 17.9083 7.92326 17.873C9.26648 20.4941 11.9952 22.2879 15.1428 22.2879C16.3358 22.2879 17.468 22.0292 18.4878 21.5658C18.6557 21.4894 18.8205 21.4076 18.9821 21.3204C19.0676 21.2743 19.1674 21.2605 19.2618 21.2842L22.7503 22.1578C23.0334 22.2287 23.2871 21.9676 23.2049 21.6898L22.221 18.3638C22.1922 18.2663 22.205 18.1617 22.254 18.0725C22.3361 17.923 22.4136 17.7707 22.4864 17.6157C22.9761 16.5729 23.25 15.4093 23.25 14.1807C23.25 10.0191 20.1134 6.58989 16.0767 6.1267C16.6443 7.23413 16.9644 8.48931 16.9644 9.81923",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var Heart = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    clipRule: "evenodd",
    d: "M6.64777 2.625C3.39458 2.625 0.75 5.34939 0.75 8.69689C0.75 15.8298 9.83255 21.253 11.706 22.3658C11.8884 22.4741 12.1117 22.4743 12.2942 22.3661C14.1686 21.2557 23.25 15.8483 23.25 8.69689C23.25 5.34939 20.6038 2.625 17.3505 2.625C13.6182 2.625 11.9992 6.03827 11.9992 6.03827C11.9992 6.03827 10.3751 2.625 6.64777 2.625Z",
    fillRule: "evenodd",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var ListTick = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M23.2357 17.3448L18.6402 21.9403L17.0999 20.3999M9 12.0002L22.5 11.9999M9 19.875H14.1671M9 4.125H22.5M6 19.875C6 21.1176 4.99264 22.125 3.75 22.125C2.50736 22.125 1.5 21.1176 1.5 19.875C1.5 18.6324 2.50736 17.625 3.75 17.625C4.99264 17.625 6 18.6324 6 19.875ZM6 4.125C6 5.36764 4.99264 6.375 3.75 6.375C2.50736 6.375 1.5 5.36764 1.5 4.125C1.5 2.88236 2.50736 1.875 3.75 1.875C4.99264 1.875 6 2.88236 6 4.125ZM6 12C6 13.2426 4.99264 14.25 3.75 14.25C2.50736 14.25 1.5 13.2426 1.5 12C1.5 10.7574 2.50736 9.75 3.75 9.75C4.99264 9.75 6 10.7574 6 12Z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var Pasta = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M3.03303 15.75C2.77106 15.1792 2.625 14.5441 2.625 13.875C2.625 11.3897 4.63972 9.37496 7.125 9.37496C7.89799 9.37496 8.62547 9.56986 9.26096 9.9132M14.0182 7.78323C13.4209 5.99865 11.7356 4.7126 9.75 4.7126C7.26472 4.7126 5.25 6.72731 5.25 9.2126C5.25 9.33911 5.25522 9.46441 5.26546 9.58829M8.25 9.2126C8.25 8.38417 8.92157 7.7126 9.75 7.7126C10.2933 7.7126 10.7691 8.00145 11.0323 8.43396M7.125 15C7.125 13.0076 7.94277 11.2062 9.26096 9.9132M21.375 15C21.375 11.0649 18.185 7.87496 14.25 7.87496C12.3073 7.87496 10.5463 8.65243 9.26096 9.9132M10.292 14.9999C10.292 12.8138 12.0642 11.0416 14.2503 11.0416C16.4365 11.0416 18.2087 12.8138 18.2087 14.9999M16.7146 8.16656L19.1666 2.5596C19.5001 1.60318 20.6198 1.17274 21.504 1.66685C22.3882 2.16095 22.6122 3.33346 21.9724 4.11871L18.4672 9.2006M2.25 15C2.25 15 2.88723 15.9821 3.48345 17.3957C4.44929 19.6856 6.51472 21.75 9 21.75H15C17.4853 21.75 19.6695 19.714 20.6167 17.4163C21.182 16.0452 21.75 15 21.75 15H2.25Z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var GutTest = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M9.375 3.74854H6.375C3.88972 3.74854 1.875 5.76325 1.875 8.24854C1.875 10.7338 3.88972 12.7485 6.375 12.7485M9.75 12.7485H6.375M6.375 12.7485H12M6.375 12.7485C3.88972 12.7485 1.875 14.7633 1.875 17.2485C1.875 19.7338 3.88972 21.7485 6.375 21.7485H11.25M11.625 17.2485H6.75M12 8.25018H6.75M16.125 2.25V1.5M11.25 1.5V2.25C11.25 3.07843 10.5784 3.75 9.75 3.75H9M15 11.25H21M21 6.73637V18.6C21 20.2569 19.6569 21.6 18 21.6C16.3431 21.6 15 20.2569 15 18.6V6.73637C15 5.91547 14.3209 5.25 13.5 5.25H22.5C21.6791 5.25 21 5.91547 21 6.73637Z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var GutHealthTest = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 31 30", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M12.2188 4.68567H8.46875C5.36215 4.68567 2.84375 7.20407 2.84375 10.3107C2.84375 13.4173 5.36215 15.9357 8.46875 15.9357M12.6875 15.9357H8.46875M8.46875 15.9357H15.5M8.46875 15.9357C5.36215 15.9357 2.84375 18.4541 2.84375 21.5607C2.84375 24.6673 5.36215 27.1857 8.46875 27.1857H14.5625M15.0312 21.5607H8.9375M15.5 10.3127H8.9375M20.6562 2.8125V1.875M14.5625 1.875V2.8125C14.5625 3.84803 13.723 4.6875 12.6875 4.6875H11.75M19.25 14.0625H26.75M26.75 8.42046V23.25C26.75 25.3211 25.0711 27 23 27C20.9289 27 19.25 25.3211 19.25 23.25V8.42046C19.25 7.39434 18.4011 6.5625 17.375 6.5625H28.625C27.5989 6.5625 26.75 7.39434 26.75 8.42046Z",
    stroke: "currentColor",
    strokeWidth: "2.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var Play = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 18 22", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx("path", { d: "M18 11L-9.78799e-07 21.3923L-7.02746e-08 0.607695L18 11Z", fill: "currentColor" }) });
var Instagram = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12 2.16094C15.2063 2.16094 15.5859 2.175 16.8469 2.23125C18.0188 2.28281 18.6516 2.47969 19.0734 2.64375C19.6313 2.85938 20.0344 3.12188 20.4516 3.53906C20.8734 3.96094 21.1313 4.35938 21.3469 4.91719C21.5109 5.33906 21.7078 5.97656 21.7594 7.14375C21.8156 8.40937 21.8297 8.78906 21.8297 11.9906C21.8297 15.1969 21.8156 15.5766 21.7594 16.8375C21.7078 18.0094 21.5109 18.6422 21.3469 19.0641C21.1313 19.6219 20.8687 20.025 20.4516 20.4422C20.0297 20.8641 19.6313 21.1219 19.0734 21.3375C18.6516 21.5016 18.0141 21.6984 16.8469 21.75C15.5813 21.8062 15.2016 21.8203 12 21.8203C8.79375 21.8203 8.41406 21.8062 7.15313 21.75C5.98125 21.6984 5.34844 21.5016 4.92656 21.3375C4.36875 21.1219 3.96563 20.8594 3.54844 20.4422C3.12656 20.0203 2.86875 19.6219 2.65313 19.0641C2.48906 18.6422 2.29219 18.0047 2.24063 16.8375C2.18438 15.5719 2.17031 15.1922 2.17031 11.9906C2.17031 8.78438 2.18438 8.40469 2.24063 7.14375C2.29219 5.97187 2.48906 5.33906 2.65313 4.91719C2.86875 4.35938 3.13125 3.95625 3.54844 3.53906C3.97031 3.11719 4.36875 2.85938 4.92656 2.64375C5.34844 2.47969 5.98594 2.28281 7.15313 2.23125C8.41406 2.175 8.79375 2.16094 12 2.16094ZM12 0C8.74219 0 8.33438 0.0140625 7.05469 0.0703125C5.77969 0.126563 4.90313 0.332812 4.14375 0.628125C3.35156 0.9375 2.68125 1.34531 2.01563 2.01562C1.34531 2.68125 0.9375 3.35156 0.628125 4.13906C0.332812 4.90313 0.126563 5.775 0.0703125 7.05C0.0140625 8.33437 0 8.74219 0 12C0 15.2578 0.0140625 15.6656 0.0703125 16.9453C0.126563 18.2203 0.332812 19.0969 0.628125 19.8563C0.9375 20.6484 1.34531 21.3188 2.01563 21.9844C2.68125 22.65 3.35156 23.0625 4.13906 23.3672C4.90313 23.6625 5.775 23.8687 7.05 23.925C8.32969 23.9812 8.7375 23.9953 11.9953 23.9953C15.2531 23.9953 15.6609 23.9812 16.9406 23.925C18.2156 23.8687 19.0922 23.6625 19.8516 23.3672C20.6391 23.0625 21.3094 22.65 21.975 21.9844C22.6406 21.3188 23.0531 20.6484 23.3578 19.8609C23.6531 19.0969 23.8594 18.225 23.9156 16.95C23.9719 15.6703 23.9859 15.2625 23.9859 12.0047C23.9859 8.74688 23.9719 8.33906 23.9156 7.05938C23.8594 5.78438 23.6531 4.90781 23.3578 4.14844C23.0625 3.35156 22.6547 2.68125 21.9844 2.01562C21.3188 1.35 20.6484 0.9375 19.8609 0.632812C19.0969 0.3375 18.225 0.13125 16.95 0.075C15.6656 0.0140625 15.2578 0 12 0Z",
      fill: "currentColor"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12 5.83594C8.59688 5.83594 5.83594 8.59688 5.83594 12C5.83594 15.4031 8.59688 18.1641 12 18.1641C15.4031 18.1641 18.1641 15.4031 18.1641 12C18.1641 8.59688 15.4031 5.83594 12 5.83594ZM12 15.9984C9.79219 15.9984 8.00156 14.2078 8.00156 12C8.00156 9.79219 9.79219 8.00156 12 8.00156C14.2078 8.00156 15.9984 9.79219 15.9984 12C15.9984 14.2078 14.2078 15.9984 12 15.9984Z",
      fill: "currentColor"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M19.8469 5.59238C19.8469 6.38926 19.2 7.03145 18.4078 7.03145C17.6109 7.03145 16.9688 6.38457 16.9688 5.59238C16.9688 4.79551 17.6156 4.15332 18.4078 4.15332C19.2 4.15332 19.8469 4.8002 19.8469 5.59238Z",
      fill: "currentColor"
    }
  )
] });
var Facebook = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M12 0C5.37264 0 0 5.37264 0 12C0 17.6275 3.87456 22.3498 9.10128 23.6467V15.6672H6.62688V12H9.10128V10.4198C9.10128 6.33552 10.9498 4.4424 14.9597 4.4424C15.72 4.4424 17.0318 4.59168 17.5685 4.74048V8.06448C17.2853 8.03472 16.7933 8.01984 16.1822 8.01984C14.2147 8.01984 13.4544 8.76528 13.4544 10.703V12H17.3741L16.7006 15.6672H13.4544V23.9122C19.3963 23.1946 24.0005 18.1354 24.0005 12C24 5.37264 18.6274 0 12 0Z",
    fill: "currentColor"
  }
) });
var Youtube = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M23.7609 7.1998C23.7609 7.1998 23.5266 5.54512 22.8047 4.81855C21.8906 3.8623 20.8688 3.85762 20.4 3.80137C17.0438 3.55762 12.0047 3.55762 12.0047 3.55762H11.9953C11.9953 3.55762 6.95625 3.55762 3.6 3.80137C3.13125 3.85762 2.10938 3.8623 1.19531 4.81855C0.473438 5.54512 0.24375 7.1998 0.24375 7.1998C0.24375 7.1998 0 9.14512 0 11.0857V12.9045C0 14.8451 0.239062 16.7904 0.239062 16.7904C0.239062 16.7904 0.473437 18.4451 1.19062 19.1717C2.10469 20.1279 3.30469 20.0951 3.83906 20.1982C5.76094 20.3811 12 20.4373 12 20.4373C12 20.4373 17.0438 20.4279 20.4 20.1889C20.8688 20.1326 21.8906 20.1279 22.8047 19.1717C23.5266 18.4451 23.7609 16.7904 23.7609 16.7904C23.7609 16.7904 24 14.8498 24 12.9045V11.0857C24 9.14512 23.7609 7.1998 23.7609 7.1998ZM9.52031 15.1123V8.36699L16.0031 11.7514L9.52031 15.1123Z",
    fill: "currentColor"
  }
) });
var ArrowRight = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, fill: "none", style, viewBox: "0 0 16 16", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    clipRule: "evenodd",
    d: "M1 8C1 7.72386 1.22386 7.5 1.5 7.5L13.2929 7.5L10.1464 4.35355C9.95118 4.15829 9.95118 3.84171 10.1464 3.64645C10.3417 3.45118 10.6583 3.45118 10.8536 3.64645L14.8536 7.64645C15.0488 7.84171 15.0488 8.15829 14.8536 8.35355L10.8536 12.3536C10.6583 12.5488 10.3417 12.5488 10.1464 12.3536C9.95118 12.1583 9.95118 11.8417 10.1464 11.6464L13.2929 8.5H1.5C1.22386 8.5 1 8.27614 1 8Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });
var Klarna = ({ style, ariaHidden }) => /* @__PURE__ */ jsxs("svg", { "aria-hidden": ariaHidden, xmlns: "http://www.w3.org/2000/svg", style, viewBox: "0 0 50 24", fill: "none", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M43.3656 0H6.63443C3.52262 0 1 3.11824 1 6.96479V17.0352C1 20.8818 3.52262 24 6.63443 24H43.3656C46.4774 24 49 20.8818 49 17.0352V6.96479C49 3.11824 46.4774 0 43.3656 0Z",
      fill: "#FFB3C7"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M39.9363 10.7574C39.4895 10.4529 38.9681 10.2761 38.4283 10.2459C37.8885 10.2156 37.3506 10.3332 36.8726 10.5859C36.3946 10.8386 35.9946 11.2169 35.7155 11.6801C35.4365 12.1432 35.2891 12.6736 35.2891 13.2143C35.2891 13.755 35.4365 14.2855 35.7155 14.7486C35.9946 15.2117 36.3946 15.59 36.8726 15.8427C37.3506 16.0954 37.8885 16.213 38.4283 16.1828C38.9681 16.1526 39.4895 15.9757 39.9363 15.6713V16.0313H41.6233V10.397H39.9363V10.7574ZM38.4021 14.664C38.1186 14.6489 37.8459 14.5511 37.6174 14.3826C37.389 14.2141 37.215 13.9824 37.1169 13.716C37.0189 13.4497 37.001 13.1604 37.0656 12.8841C37.1302 12.6077 37.2744 12.3563 37.4803 12.161C37.6863 11.9657 37.945 11.8351 38.2244 11.7853C38.5038 11.7354 38.7917 11.7686 39.0525 11.8807C39.3132 11.9928 39.5354 12.1789 39.6915 12.4159C39.8476 12.653 39.9308 12.9306 39.9308 13.2144C39.92 13.6093 39.753 13.9837 39.4664 14.2555C39.1797 14.5273 38.7969 14.6742 38.4021 14.664Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx("path", { d: "M16.8006 7.88574H15.0352V16.0311H16.8006V7.88574Z", fill: "black" }),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M32.2926 10.2453C31.9609 10.2257 31.6294 10.287 31.3267 10.424C31.024 10.561 30.7592 10.7695 30.555 11.0317V10.3971H28.875V16.0312H30.5752V13.0703C30.5621 12.9002 30.5859 12.7293 30.6447 12.5692C30.7036 12.4091 30.7963 12.2635 30.9164 12.1424C31.0366 12.0214 31.1814 11.9275 31.341 11.8674C31.5007 11.8073 31.6714 11.7822 31.8416 11.7939C32.583 11.7939 33.0092 12.2369 33.0092 13.0586V16.0312H34.6943V12.4482C34.6943 11.137 33.6517 10.2453 32.2926 10.2453Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M22.1395 10.7574C21.6927 10.4529 21.1713 10.2761 20.6314 10.2459C20.0916 10.2156 19.5537 10.3332 19.0757 10.5859C18.5977 10.8386 18.1977 11.2169 17.9187 11.6801C17.6396 12.1432 17.4922 12.6736 17.4922 13.2143C17.4922 13.755 17.6396 14.2855 17.9187 14.7486C18.1977 15.2117 18.5977 15.59 19.0757 15.8427C19.5537 16.0954 20.0916 16.213 20.6314 16.1828C21.1713 16.1526 21.6927 15.9757 22.1395 15.6713V16.0313H23.8264V10.397H22.1395V10.7574ZM20.6052 14.664C20.3217 14.649 20.0489 14.5512 19.8205 14.3827C19.592 14.2142 19.418 13.9825 19.3199 13.7162C19.2218 13.4498 19.2039 13.1606 19.2684 12.8842C19.333 12.6078 19.4772 12.3564 19.6831 12.1611C19.8891 11.9657 20.1478 11.8351 20.4272 11.7853C20.7066 11.7354 20.9945 11.7686 21.2553 11.8807C21.5161 11.9928 21.7383 12.1789 21.8944 12.4159C22.0505 12.653 22.1337 12.9306 22.1337 13.2144C22.1229 13.6092 21.9559 13.9837 21.6693 14.2554C21.3827 14.5272 21 14.6742 20.6051 14.664H20.6052Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M26.4342 11.1308V10.3969H24.707V16.0313H26.4379V13.4009C26.4379 12.5133 27.3999 12.0362 28.0675 12.0362C28.0742 12.0362 28.0807 12.0369 28.0875 12.037V10.3972C27.7747 10.3888 27.464 10.4503 27.178 10.5772C26.892 10.7041 26.6379 10.8932 26.4342 11.1308Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M43.4143 14.0327C43.2048 14.0327 43.0001 14.0948 42.826 14.2112C42.6519 14.3275 42.5162 14.4929 42.4361 14.6864C42.3559 14.8799 42.335 15.0928 42.3758 15.2981C42.4167 15.5035 42.5175 15.6922 42.6656 15.8403C42.8137 15.9883 43.0024 16.0892 43.2077 16.13C43.4131 16.1709 43.626 16.1499 43.8195 16.0698C44.013 15.9896 44.1783 15.8539 44.2947 15.6798C44.411 15.5057 44.4731 15.301 44.4731 15.0915C44.4731 14.9525 44.4458 14.8148 44.3926 14.6863C44.3393 14.5579 44.2613 14.4411 44.163 14.3428C44.0647 14.2445 43.948 14.1665 43.8195 14.1133C43.691 14.0601 43.5533 14.0327 43.4143 14.0327Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M13.8173 7.88428H11.9877C11.9905 8.618 11.8208 9.34212 11.4921 9.99814C11.1635 10.6542 10.6852 11.2237 10.0959 11.6608L9.37109 12.2037L12.1797 16.0337H14.4888L11.9046 12.5096C12.5141 11.9038 12.997 11.183 13.3254 10.389C13.6537 9.59492 13.8209 8.74352 13.8173 7.88428Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx("path", { d: "M9.14929 7.88428H7.31641V16.0334H9.14929V7.88428Z", fill: "black" })
] });
var Test = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, style, viewBox: "0 0 17 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M7.09961 5.59961H14.0996M3.09961 10.9496H7.09961M14.0996 2.34961V11.5996C14.0996 12.7042 13.2042 13.5996 12.0996 13.5996C10.995 13.5996 10.0996 12.7042 10.0996 11.5996V2.34961C10.0996 1.79732 9.65189 1.34961 9.09961 1.34961H15.0996C14.5473 1.34961 14.0996 1.79732 14.0996 2.34961ZM7.09961 6.36325V12.8496C7.09961 13.9542 6.20418 14.8496 5.09961 14.8496C3.99504 14.8496 3.09961 13.9542 3.09961 12.8496V6.36325C3.09961 5.9415 2.75772 5.59961 2.33597 5.59961H2.09961H8.09961H7.86325C7.4415 5.59961 7.09961 5.9415 7.09961 6.36325Z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var DietVegetarian = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, style, viewBox: "0 0 17 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M1.74979 2.5C8.34207 6.56471 8.73841 14.2252 8.73841 14.2252C8.73841 14.2252 8.75878 10.0016 12.2912 6.4106M15.1614 2.28418C11.0754 2.49586 8.44275 5.67113 9.60184 10.2528C9.60569 10.268 9.61055 10.2833 9.61792 10.2971C9.67136 10.3973 9.78117 10.4572 9.89557 10.4466C14.52 9.50559 16.315 5.90622 15.4214 2.49621C15.3901 2.37672 15.2848 2.29007 15.1614 2.28418Z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var BloodSugar = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, style, viewBox: "0 0 31 30", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M14.7878 25.6268C13.4467 26.4905 11.8389 26.9933 10.1106 26.9933C5.42447 26.9933 1.62427 23.2969 1.62427 18.7339C1.62427 14.931 7.5176 6.69986 9.4813 4.06111C9.79148 3.6463 10.4297 3.6463 10.7399 4.06111C11.605 5.22496 13.2354 7.47671 14.7878 9.95658M19.2491 14.0625H26.7491M26.7491 8.42046V23.25C26.7491 25.3211 25.0702 27 22.9991 27C20.928 27 19.2491 25.3211 19.2491 23.25V8.42046C19.2491 7.39434 18.4002 6.5625 17.3741 6.5625H28.6241C27.598 6.5625 26.7491 7.39434 26.7491 8.42046Z",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var BloodFat = ({ style, ariaHidden }) => /* @__PURE__ */ jsx("svg", { "aria-hidden": ariaHidden, style, viewBox: "0 0 31 30", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M14.1239 8.4375C12.8997 6.58188 11.7372 4.98139 11.0531 4.06111C10.7429 3.6463 10.1047 3.6463 9.79453 4.06111C7.83083 6.69986 1.9375 14.931 1.9375 18.7339C1.9375 22.7307 4.85323 26.0627 8.72608 26.828M22.5221 11.3292C22.4416 11.4401 22.3683 11.554 22.3022 11.6704C22.1867 11.8737 21.9743 12.0088 21.7405 12.0094C20.55 12.0126 19.3682 12.2835 18.2908 12.8072C18.1128 12.8937 17.9035 12.888 17.7278 12.7969C15.4633 11.6227 12.6188 12.2485 11.0729 14.3762C9.36909 16.7213 9.88783 20.0027 12.2315 21.7055C12.8567 22.1598 13.5487 22.4558 14.2596 22.6011C14.296 22.6086 14.326 22.6342 14.3389 22.669C14.3566 22.7169 14.3379 22.7706 14.2951 22.7985C14.0516 22.957 13.8324 23.1618 13.6515 23.4108C12.8579 24.5031 13.1001 26.0318 14.1923 26.8253C15.0661 27.4601 16.2192 27.4322 17.0518 26.8364C17.2741 26.6772 17.5733 26.6374 17.8101 26.774C21.271 28.7711 25.752 27.8718 28.153 24.5672C30.0684 21.9309 30.1187 18.5085 28.5534 15.8742C28.4338 15.6729 28.4409 15.4208 28.5567 15.2173C29.4511 13.6467 29.0398 11.6211 27.5434 10.5339C25.9372 9.36693 23.6891 9.72299 22.5221 11.3292Z",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var PersonInfo = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "25",
    height: "24",
    viewBox: "0 0 25 24",
    fill: "none",
    "aria-hidden": ariaHidden,
    style,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M5.47542 10.234C3.30461 11.4366 1.81347 13.844 1.38033 16.5622C1.21284 17.6132 1.62683 18.6605 2.49146 19.2812C3.76843 20.1979 6.05824 21.4101 9.48672 21.695M16.5254 10.234C17.0272 10.512 17.4927 10.8543 17.9171 11.25M23.7507 19.5H13.2507M23.7507 15.75H13.2507M23.7507 23.25H13.2507M11.0004 12.9375C14.3659 12.9375 17.0942 10.2092 17.0942 6.84375C17.0942 3.47826 14.3659 0.75 11.0004 0.75C7.63493 0.75 4.90667 3.47826 4.90667 6.84375C4.90667 10.2092 7.63493 12.9375 11.0004 12.9375Z",
        stroke: "currentColor",
        strokeWidth: "1.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var BoxInfo = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "25",
    height: "24",
    viewBox: "0 0 25 24",
    fill: "none",
    "aria-hidden": ariaHidden,
    style,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M3.4998 8.69961V18.8484C3.4998 19.0548 3.64033 19.2348 3.84066 19.2849L6.5 19.9497M11.8814 3.45776H3.76564C3.60165 3.45776 3.45064 3.54697 3.37151 3.6906L1.30908 7.43376C1.1659 7.69362 1.30898 8.01849 1.59735 8.08829L9.91508 10.1015C10.1439 10.1569 10.3767 10.0266 10.4491 9.80254L12.3096 4.04616L12.4998 3.44971M13.1182 3.45776H21.2339C21.3979 3.45776 21.5489 3.54698 21.6281 3.69062L22.7165 5.66629M4.9998 3.44971H12.4998M12.4998 3.44971H19.9998M12.4998 3.44971L12.69 4.04616L14.2911 9M21.7734 14.4152L17.5872 10.229M23.5969 11.006L20.9964 8.40557C20.5586 7.96767 19.8486 7.96767 19.4107 8.40557L9.81003 18.0062C9.76307 18.0532 9.73106 18.113 9.71803 18.1781L8.79041 22.8162C8.74333 23.0516 8.95086 23.2591 9.18624 23.2121L13.8244 22.2844C13.8895 22.2714 13.9493 22.2394 13.9962 22.1924L23.5969 12.5918C24.0348 12.1539 24.0348 11.4439 23.5969 11.006Z",
        stroke: "currentColor",
        strokeWidth: "1.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var PaymentHistory = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "25",
    height: "24",
    viewBox: "0 0 25 24",
    fill: "none",
    "aria-hidden": ariaHidden,
    style,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M7.25 11.6251L17.75 11.625M7.25 15.375H17.75M7.25 7.875H17.75M3.5 21.3496C3.5 21.6951 3.87327 21.9117 4.17326 21.7403L7.86484 19.6308C8.39909 19.3255 9.0656 19.3775 9.54609 19.7619L11.563 21.3754C12.1108 21.8136 12.8892 21.8136 13.437 21.3754L15.4539 19.7619C15.9344 19.3775 16.6009 19.3255 17.1352 19.6308L20.8267 21.7403C21.1267 21.9117 21.5 21.6951 21.5 21.3496V4.125C21.5 3.29657 20.8284 2.625 20 2.625H5C4.17157 2.625 3.5 3.29657 3.5 4.125V21.3496Z",
        stroke: "currentColor",
        strokeWidth: "1.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var Support = ({ style, ariaHidden }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "25",
    height: "24",
    viewBox: "0 0 25 24",
    fill: "none",
    "aria-hidden": ariaHidden,
    style,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M20 10.125V17.625C22.0711 17.625 23.75 15.9461 23.75 13.875C23.75 11.8039 22.0711 10.125 20 10.125ZM20 10.125C20 10.0762 19.9995 9.27747 19.9986 9.22887C19.9207 5.15418 16.5933 1.875 12.5 1.875C8.40669 1.875 5.07926 5.15418 5.0014 9.22887C5.00047 9.27747 5 10.0762 5 10.125M5 17.625V10.125M5 17.625L9.20711 21.8321C9.39464 22.0196 9.649 22.125 9.91421 22.125H12.5M5 17.625C2.92893 17.625 1.25 15.9461 1.25 13.875C1.25 11.8039 2.92893 10.125 5 10.125",
        stroke: "currentColor",
        strokeWidth: "1.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ALL_ICONS = {
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "checkbox-tick": CheckboxTick,
  close: Close,
  delivery: Delivery,
  email: Email,
  minus: Minus,
  plus: Plus,
  "radio-selected": RadioSelected,
  "radio-unselected": RadioUnselected,
  "tick-in-circle-no-fill": TickInCircleNoFill,
  "tick-in-star": TickInStar,
  "volume-mute": VolumeMute,
  "info-circle": InfoCircle,
  "hand-wave-lines": HandWaveLines,
  phone: Phone,
  community: Community,
  heart: Heart,
  pasta: Pasta,
  gut_test: GutTest,
  "gut-health-test": GutHealthTest,
  list_tick: ListTick,
  play: Play,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  "arrow-right": ArrowRight,
  klarna: Klarna,
  test: Test,
  "diet-vegetarian": DietVegetarian,
  "blood-sugar": BloodSugar,
  "blood-fat": BloodFat,
  "person-info": PersonInfo,
  "box-info": BoxInfo,
  "payment-history": PaymentHistory,
  support: Support
};
var isValidIconName = (value) => {
  return value in ALL_ICONS;
};
function DSIcon({ name: iconName, size, color, ariaHidden, testId }) {
  if (!isValidIconName(iconName)) {
    return null;
  }
  const IconComponent = ALL_ICONS[iconName];
  return /* @__PURE__ */ jsx(
    IconComponent,
    __spreadProps(__spreadValues({}, testId ? { "data-testid": testId } : {}), {
      style: {
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        color: color ? `var(--color-icon-${color})` : "var(--color-text-primary)"
      },
      ariaHidden
    })
  );
}
var StyledAccordionItem = styled.div`
  width: 100%;
  position: relative;
  user-select: none;
`;
var StyledAccordionContent = styled.div`
  max-height: 0;
  transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
  overflow: hidden;
  user-select: text;

  &.active {
    max-height: 10000px;
    transition: max-height 1s ease-in-out;
  }
`;
var StyledAccordionItemHeader = styled.div`
  cursor: pointer;
`;
var StyledIconAnimated = styled.div`
  position: absolute;
  right: 0;
  padding-top: 4px;
  transition: opacity 0.2s ease-in-out;
  opacity: 0;
  &.active {
    opacity: 1;
  }
`;
var StyledHiddenAnchor = styled.div`
  position: absolute;
  top: -100px;
`;
var isInViewport = (headerRef, offset = 0) => {
  const top = headerRef.getBoundingClientRect().top;
  return top + offset >= 0 && top - offset <= window.innerHeight;
};
var AccordionItem = ({ onToggle, isOpen, content, title, id, variant }) => {
  const headerRef = useRef(null);
  const [isInitialRender, setIsInitialRender] = useState(true);
  useEffect(() => {
    if (isOpen && headerRef.current && !isInitialRender) {
      setTimeout(() => {
        if (headerRef.current && !isInViewport(headerRef.current)) {
          headerRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 500);
    }
    setIsInitialRender(false);
  }, [open]);
  return /* @__PURE__ */ jsxs(StyledAccordionItem, { id, children: [
    /* @__PURE__ */ jsx(StyledHiddenAnchor, { ref: headerRef }),
    /* @__PURE__ */ jsx(DSDivider, {}),
    /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 24 }),
    /* @__PURE__ */ jsx(StyledAccordionItemHeader, { onClick: onToggle, children: /* @__PURE__ */ jsxs(DSFlex, { alignItems: "center", direction: "row", justifyContent: "space-between", children: [
      /* @__PURE__ */ jsxs(DSFlex, { alignItems: "center", direction: "row", justifyContent: "flex-start", children: [
        typeof title === "string" ? /* @__PURE__ */ jsx(DSText, { variant: "fluid-paragraph-300", weight: "light", children: title }) : title,
        /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: 32 })
      ] }),
      /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(StyledIconAnimated, { className: isOpen ? "active" : "", children: /* @__PURE__ */ jsx(DSIcon, { name: variant === "chevron" ? "chevron-up" : "minus", size: 16 }) }),
        /* @__PURE__ */ jsx(StyledIconAnimated, { className: !isOpen ? "active" : "", children: /* @__PURE__ */ jsx(DSIcon, { name: variant === "chevron" ? "chevron-down" : "plus", size: 16 }) })
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs(StyledAccordionContent, { className: isOpen ? "active" : void 0, children: [
      /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 24 }),
      content
    ] }),
    /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 24 })
  ] });
};
var DSAccordion = ({
  items,
  variant = "default",
  onItemClick,
  expandFirstSection,
  testId
}) => {
  const [expandedSectionId, setExpandedSectionId] = useState(
    expandFirstSection ? items[0].id : void 0
  );
  const onToggleOpen = useCallback(
    (selectedSection) => {
      if (selectedSection.id === expandedSectionId) {
        setExpandedSectionId(void 0);
      } else {
        setExpandedSectionId(selectedSection.id);
        onItemClick == null ? void 0 : onItemClick(selectedSection);
      }
    },
    [expandedSectionId, onItemClick]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    items.map((item) => /* @__PURE__ */ jsx(React2.Fragment, { children: /* @__PURE__ */ jsx(
      AccordionItem,
      __spreadValues({
        id: item.id,
        onToggle: () => {
          onToggleOpen(item);
        },
        isOpen: expandedSectionId === item.id,
        title: item.title,
        variant,
        content: item.content
      }, testId ? { "data-testid": `${testId}-${item.id}` } : {})
    ) }, item.id)),
    /* @__PURE__ */ jsx(DSDivider, {})
  ] });
};
var getPadding = (size) => {
  switch (size) {
    case "large":
      return "var(--grid-8) var(--grid-12)";
    case "small":
      return "var(--grid-2) var(--grid-4)";
    case "regular":
    default:
      return "var(--grid-4) var(--grid-8)";
  }
};
var StyledBadge = styled.span`
  ${({ $surfaceColor }) => `background-color: var(--color-surface-${$surfaceColor});`}
  ${({ $size }) => `padding: ${getPadding($size)};`}
  border-radius: ${({ $size }) => $size === "small" ? "var(--radius-4)" : "var(--radius-8)"};
  display: inline-flex;
`;
var DSBadge = ({
  textColor = "primary",
  surfaceColor = "neutral",
  size = "regular",
  children,
  testId
}) => {
  return /* @__PURE__ */ jsx(StyledBadge, __spreadProps(__spreadValues({ $surfaceColor: surfaceColor, $size: size }, testId ? { "data-testid": testId } : {}), { children: /* @__PURE__ */ jsx(DSText, { variant: "fluid-label-100", color: textColor, weight: size === "small" ? "light" : "regular", children }) }));
};

// node_modules/@internationalized/date/dist/utils.mjs
function $2b4dce13dd5a17fa$export$842a2cf37af977e1(amount, numerator) {
  return amount - numerator * Math.floor(amount / numerator);
}

// node_modules/@internationalized/date/dist/GregorianCalendar.mjs
var $3b62074eb05584b2$var$EPOCH = 1721426;
function $3b62074eb05584b2$export$f297eb839006d339(era, year, month, day) {
  year = $3b62074eb05584b2$export$c36e0ecb2d4fa69d(era, year);
  let y1 = year - 1;
  let monthOffset = -2;
  if (month <= 2)
    monthOffset = 0;
  else if ($3b62074eb05584b2$export$553d7fa8e3805fc0(year))
    monthOffset = -1;
  return $3b62074eb05584b2$var$EPOCH - 1 + 365 * y1 + Math.floor(y1 / 4) - Math.floor(y1 / 100) + Math.floor(y1 / 400) + Math.floor((367 * month - 362) / 12 + monthOffset + day);
}
function $3b62074eb05584b2$export$553d7fa8e3805fc0(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}
function $3b62074eb05584b2$export$c36e0ecb2d4fa69d(era, year) {
  return era === "BC" ? 1 - year : year;
}
function $3b62074eb05584b2$export$4475b7e617eb123c(year) {
  let era = "AD";
  if (year <= 0) {
    era = "BC";
    year = 1 - year;
  }
  return [
    era,
    year
  ];
}
var $3b62074eb05584b2$var$daysInMonth = {
  standard: [
    31,
    28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31
  ],
  leapyear: [
    31,
    29,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31
  ]
};
var $3b62074eb05584b2$export$80ee6245ec4f29ec = class {
  fromJulianDay(jd) {
    let jd0 = jd;
    let depoch = jd0 - $3b62074eb05584b2$var$EPOCH;
    let quadricent = Math.floor(depoch / 146097);
    let dqc = ($2b4dce13dd5a17fa$export$842a2cf37af977e1)(depoch, 146097);
    let cent = Math.floor(dqc / 36524);
    let dcent = ($2b4dce13dd5a17fa$export$842a2cf37af977e1)(dqc, 36524);
    let quad = Math.floor(dcent / 1461);
    let dquad = ($2b4dce13dd5a17fa$export$842a2cf37af977e1)(dcent, 1461);
    let yindex = Math.floor(dquad / 365);
    let extendedYear = quadricent * 400 + cent * 100 + quad * 4 + yindex + (cent !== 4 && yindex !== 4 ? 1 : 0);
    let [era, year] = $3b62074eb05584b2$export$4475b7e617eb123c(extendedYear);
    let yearDay = jd0 - $3b62074eb05584b2$export$f297eb839006d339(era, year, 1, 1);
    let leapAdj = 2;
    if (jd0 < $3b62074eb05584b2$export$f297eb839006d339(era, year, 3, 1))
      leapAdj = 0;
    else if ($3b62074eb05584b2$export$553d7fa8e3805fc0(year))
      leapAdj = 1;
    let month = Math.floor(((yearDay + leapAdj) * 12 + 373) / 367);
    let day = jd0 - $3b62074eb05584b2$export$f297eb839006d339(era, year, month, 1) + 1;
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(era, year, month, day);
  }
  toJulianDay(date) {
    return $3b62074eb05584b2$export$f297eb839006d339(date.era, date.year, date.month, date.day);
  }
  getDaysInMonth(date) {
    return $3b62074eb05584b2$var$daysInMonth[$3b62074eb05584b2$export$553d7fa8e3805fc0(date.year) ? "leapyear" : "standard"][date.month - 1];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getMonthsInYear(date) {
    return 12;
  }
  getDaysInYear(date) {
    return $3b62074eb05584b2$export$553d7fa8e3805fc0(date.year) ? 366 : 365;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getYearsInEra(date) {
    return 9999;
  }
  getEras() {
    return [
      "BC",
      "AD"
    ];
  }
  isInverseEra(date) {
    return date.era === "BC";
  }
  balanceDate(date) {
    if (date.year <= 0) {
      date.era = date.era === "BC" ? "AD" : "BC";
      date.year = 1 - date.year;
    }
  }
  constructor() {
    this.identifier = "gregory";
  }
};

// node_modules/@internationalized/date/dist/queries.mjs
function $14e0f24ef4ac5c92$export$461939dd4422153(timeZone) {
  return ($11d87f3f76e88657$export$1b96692a1ba042ac)(Date.now(), timeZone);
}
function $14e0f24ef4ac5c92$export$d0bdf45af03a6ea3(timeZone) {
  return ($11d87f3f76e88657$export$93522d1a439f3617)($14e0f24ef4ac5c92$export$461939dd4422153(timeZone));
}
function $14e0f24ef4ac5c92$export$68781ddf31c0090f(a, b) {
  return a.calendar.toJulianDay(a) - b.calendar.toJulianDay(b);
}
function $14e0f24ef4ac5c92$export$c19a80a9721b80f6(a, b) {
  return $14e0f24ef4ac5c92$var$timeToMs(a) - $14e0f24ef4ac5c92$var$timeToMs(b);
}
function $14e0f24ef4ac5c92$var$timeToMs(a) {
  return a.hour * 36e5 + a.minute * 6e4 + a.second * 1e3 + a.millisecond;
}
var $14e0f24ef4ac5c92$var$localTimeZone = null;
function $14e0f24ef4ac5c92$export$aa8b41735afcabd2() {
  if ($14e0f24ef4ac5c92$var$localTimeZone == null)
    $14e0f24ef4ac5c92$var$localTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  return $14e0f24ef4ac5c92$var$localTimeZone;
}

// node_modules/@internationalized/date/dist/conversion.mjs
function $11d87f3f76e88657$export$bd4fb2bc8bb06fb(date) {
  date = $11d87f3f76e88657$export$b4a036af3fc0b032(date, new ($3b62074eb05584b2$export$80ee6245ec4f29ec)());
  let year = ($3b62074eb05584b2$export$c36e0ecb2d4fa69d)(date.era, date.year);
  return $11d87f3f76e88657$var$epochFromParts(year, date.month, date.day, date.hour, date.minute, date.second, date.millisecond);
}
function $11d87f3f76e88657$var$epochFromParts(year, month, day, hour, minute, second, millisecond) {
  let date = /* @__PURE__ */ new Date();
  date.setUTCHours(hour, minute, second, millisecond);
  date.setUTCFullYear(year, month - 1, day);
  return date.getTime();
}
function $11d87f3f76e88657$export$59c99f3515d3493f(ms, timeZone) {
  if (timeZone === "UTC")
    return 0;
  if (ms > 0 && timeZone === ($14e0f24ef4ac5c92$export$aa8b41735afcabd2)())
    return new Date(ms).getTimezoneOffset() * -6e4;
  let { year, month, day, hour, minute, second } = $11d87f3f76e88657$var$getTimeZoneParts(ms, timeZone);
  let utc = $11d87f3f76e88657$var$epochFromParts(year, month, day, hour, minute, second, 0);
  return utc - Math.floor(ms / 1e3) * 1e3;
}
var $11d87f3f76e88657$var$formattersByTimeZone = /* @__PURE__ */ new Map();
function $11d87f3f76e88657$var$getTimeZoneParts(ms, timeZone) {
  let formatter = $11d87f3f76e88657$var$formattersByTimeZone.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      era: "short",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    });
    $11d87f3f76e88657$var$formattersByTimeZone.set(timeZone, formatter);
  }
  let parts = formatter.formatToParts(new Date(ms));
  let namedParts = {};
  for (let part of parts)
    if (part.type !== "literal")
      namedParts[part.type] = part.value;
  return {
    // Firefox returns B instead of BC... https://bugzilla.mozilla.org/show_bug.cgi?id=1752253
    year: namedParts.era === "BC" || namedParts.era === "B" ? -namedParts.year + 1 : +namedParts.year,
    month: +namedParts.month,
    day: +namedParts.day,
    hour: namedParts.hour === "24" ? 0 : +namedParts.hour,
    minute: +namedParts.minute,
    second: +namedParts.second
  };
}
var $11d87f3f76e88657$var$DAYMILLIS = 864e5;
function $11d87f3f76e88657$var$getValidWallTimes(date, timeZone, earlier, later) {
  let found = earlier === later ? [
    earlier
  ] : [
    earlier,
    later
  ];
  return found.filter((absolute) => $11d87f3f76e88657$var$isValidWallTime(date, timeZone, absolute));
}
function $11d87f3f76e88657$var$isValidWallTime(date, timeZone, absolute) {
  let parts = $11d87f3f76e88657$var$getTimeZoneParts(absolute, timeZone);
  return date.year === parts.year && date.month === parts.month && date.day === parts.day && date.hour === parts.hour && date.minute === parts.minute && date.second === parts.second;
}
function $11d87f3f76e88657$export$5107c82f94518f5c(date, timeZone, disambiguation = "compatible") {
  let dateTime = $11d87f3f76e88657$export$b21e0b124e224484(date);
  if (timeZone === "UTC")
    return $11d87f3f76e88657$export$bd4fb2bc8bb06fb(dateTime);
  if (timeZone === ($14e0f24ef4ac5c92$export$aa8b41735afcabd2)() && disambiguation === "compatible") {
    dateTime = $11d87f3f76e88657$export$b4a036af3fc0b032(dateTime, new ($3b62074eb05584b2$export$80ee6245ec4f29ec)());
    let date2 = /* @__PURE__ */ new Date();
    let year = ($3b62074eb05584b2$export$c36e0ecb2d4fa69d)(dateTime.era, dateTime.year);
    date2.setFullYear(year, dateTime.month - 1, dateTime.day);
    date2.setHours(dateTime.hour, dateTime.minute, dateTime.second, dateTime.millisecond);
    return date2.getTime();
  }
  let ms = $11d87f3f76e88657$export$bd4fb2bc8bb06fb(dateTime);
  let offsetBefore = $11d87f3f76e88657$export$59c99f3515d3493f(ms - $11d87f3f76e88657$var$DAYMILLIS, timeZone);
  let offsetAfter = $11d87f3f76e88657$export$59c99f3515d3493f(ms + $11d87f3f76e88657$var$DAYMILLIS, timeZone);
  let valid = $11d87f3f76e88657$var$getValidWallTimes(dateTime, timeZone, ms - offsetBefore, ms - offsetAfter);
  if (valid.length === 1)
    return valid[0];
  if (valid.length > 1)
    switch (disambiguation) {
      case "compatible":
      case "earlier":
        return valid[0];
      case "later":
        return valid[valid.length - 1];
      case "reject":
        throw new RangeError("Multiple possible absolute times found");
    }
  switch (disambiguation) {
    case "earlier":
      return Math.min(ms - offsetBefore, ms - offsetAfter);
    case "compatible":
    case "later":
      return Math.max(ms - offsetBefore, ms - offsetAfter);
    case "reject":
      throw new RangeError("No such absolute time found");
  }
}
function $11d87f3f76e88657$export$e67a095c620b86fe(dateTime, timeZone, disambiguation = "compatible") {
  return new Date($11d87f3f76e88657$export$5107c82f94518f5c(dateTime, timeZone, disambiguation));
}
function $11d87f3f76e88657$export$1b96692a1ba042ac(ms, timeZone) {
  let offset = $11d87f3f76e88657$export$59c99f3515d3493f(ms, timeZone);
  let date = new Date(ms + offset);
  let year = date.getUTCFullYear();
  let month = date.getUTCMonth() + 1;
  let day = date.getUTCDate();
  let hour = date.getUTCHours();
  let minute = date.getUTCMinutes();
  let second = date.getUTCSeconds();
  let millisecond = date.getUTCMilliseconds();
  return new ($35ea8db9cb2ccb90$export$d3b7288e7994edea)(year < 1 ? "BC" : "AD", year < 1 ? -year + 1 : year, month, day, timeZone, offset, hour, minute, second, millisecond);
}
function $11d87f3f76e88657$export$93522d1a439f3617(dateTime) {
  return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(dateTime.calendar, dateTime.era, dateTime.year, dateTime.month, dateTime.day);
}
function $11d87f3f76e88657$export$b21e0b124e224484(date, time) {
  let hour = 0, minute = 0, second = 0, millisecond = 0;
  if ("timeZone" in date)
    ({ hour, minute, second, millisecond } = date);
  else if ("hour" in date && !time)
    return date;
  if (time)
    ({ hour, minute, second, millisecond } = time);
  return new ($35ea8db9cb2ccb90$export$ca871e8dbb80966f)(date.calendar, date.era, date.year, date.month, date.day, hour, minute, second, millisecond);
}
function $11d87f3f76e88657$export$b4a036af3fc0b032(date, calendar) {
  if (date.calendar.identifier === calendar.identifier)
    return date;
  let calendarDate = calendar.fromJulianDay(date.calendar.toJulianDay(date));
  let copy = date.copy();
  copy.calendar = calendar;
  copy.era = calendarDate.era;
  copy.year = calendarDate.year;
  copy.month = calendarDate.month;
  copy.day = calendarDate.day;
  ($735220c2d4774dd3$export$c4e2ecac49351ef2)(copy);
  return copy;
}
function $11d87f3f76e88657$export$84c95a83c799e074(date, timeZone, disambiguation) {
  if (date instanceof ($35ea8db9cb2ccb90$export$d3b7288e7994edea)) {
    if (date.timeZone === timeZone)
      return date;
    return $11d87f3f76e88657$export$538b00033cc11c75(date, timeZone);
  }
  let ms = $11d87f3f76e88657$export$5107c82f94518f5c(date, timeZone, disambiguation);
  return $11d87f3f76e88657$export$1b96692a1ba042ac(ms, timeZone);
}
function $11d87f3f76e88657$export$83aac07b4c37b25(date) {
  let ms = $11d87f3f76e88657$export$bd4fb2bc8bb06fb(date) - date.offset;
  return new Date(ms);
}
function $11d87f3f76e88657$export$538b00033cc11c75(date, timeZone) {
  let ms = $11d87f3f76e88657$export$bd4fb2bc8bb06fb(date) - date.offset;
  return $11d87f3f76e88657$export$b4a036af3fc0b032($11d87f3f76e88657$export$1b96692a1ba042ac(ms, timeZone), date.calendar);
}

// node_modules/@internationalized/date/dist/manipulation.mjs
var $735220c2d4774dd3$var$ONE_HOUR = 36e5;
function $735220c2d4774dd3$export$e16d8520af44a096(date, duration) {
  let mutableDate = date.copy();
  let days = "hour" in mutableDate ? $735220c2d4774dd3$var$addTimeFields(mutableDate, duration) : 0;
  $735220c2d4774dd3$var$addYears(mutableDate, duration.years || 0);
  if (mutableDate.calendar.balanceYearMonth)
    mutableDate.calendar.balanceYearMonth(mutableDate, date);
  mutableDate.month += duration.months || 0;
  $735220c2d4774dd3$var$balanceYearMonth(mutableDate);
  $735220c2d4774dd3$var$constrainMonthDay(mutableDate);
  mutableDate.day += (duration.weeks || 0) * 7;
  mutableDate.day += duration.days || 0;
  mutableDate.day += days;
  $735220c2d4774dd3$var$balanceDay(mutableDate);
  if (mutableDate.calendar.balanceDate)
    mutableDate.calendar.balanceDate(mutableDate);
  if (mutableDate.year < 1) {
    mutableDate.year = 1;
    mutableDate.month = 1;
    mutableDate.day = 1;
  }
  let maxYear = mutableDate.calendar.getYearsInEra(mutableDate);
  if (mutableDate.year > maxYear) {
    var _mutableDate_calendar_isInverseEra, _mutableDate_calendar;
    let isInverseEra = (_mutableDate_calendar_isInverseEra = (_mutableDate_calendar = mutableDate.calendar).isInverseEra) === null || _mutableDate_calendar_isInverseEra === void 0 ? void 0 : _mutableDate_calendar_isInverseEra.call(_mutableDate_calendar, mutableDate);
    mutableDate.year = maxYear;
    mutableDate.month = isInverseEra ? 1 : mutableDate.calendar.getMonthsInYear(mutableDate);
    mutableDate.day = isInverseEra ? 1 : mutableDate.calendar.getDaysInMonth(mutableDate);
  }
  if (mutableDate.month < 1) {
    mutableDate.month = 1;
    mutableDate.day = 1;
  }
  let maxMonth = mutableDate.calendar.getMonthsInYear(mutableDate);
  if (mutableDate.month > maxMonth) {
    mutableDate.month = maxMonth;
    mutableDate.day = mutableDate.calendar.getDaysInMonth(mutableDate);
  }
  mutableDate.day = Math.max(1, Math.min(mutableDate.calendar.getDaysInMonth(mutableDate), mutableDate.day));
  return mutableDate;
}
function $735220c2d4774dd3$var$addYears(date, years) {
  var _date_calendar_isInverseEra, _date_calendar;
  if ((_date_calendar_isInverseEra = (_date_calendar = date.calendar).isInverseEra) === null || _date_calendar_isInverseEra === void 0 ? void 0 : _date_calendar_isInverseEra.call(_date_calendar, date))
    years = -years;
  date.year += years;
}
function $735220c2d4774dd3$var$balanceYearMonth(date) {
  while (date.month < 1) {
    $735220c2d4774dd3$var$addYears(date, -1);
    date.month += date.calendar.getMonthsInYear(date);
  }
  let monthsInYear = 0;
  while (date.month > (monthsInYear = date.calendar.getMonthsInYear(date))) {
    date.month -= monthsInYear;
    $735220c2d4774dd3$var$addYears(date, 1);
  }
}
function $735220c2d4774dd3$var$balanceDay(date) {
  while (date.day < 1) {
    date.month--;
    $735220c2d4774dd3$var$balanceYearMonth(date);
    date.day += date.calendar.getDaysInMonth(date);
  }
  while (date.day > date.calendar.getDaysInMonth(date)) {
    date.day -= date.calendar.getDaysInMonth(date);
    date.month++;
    $735220c2d4774dd3$var$balanceYearMonth(date);
  }
}
function $735220c2d4774dd3$var$constrainMonthDay(date) {
  date.month = Math.max(1, Math.min(date.calendar.getMonthsInYear(date), date.month));
  date.day = Math.max(1, Math.min(date.calendar.getDaysInMonth(date), date.day));
}
function $735220c2d4774dd3$export$c4e2ecac49351ef2(date) {
  if (date.calendar.constrainDate)
    date.calendar.constrainDate(date);
  date.year = Math.max(1, Math.min(date.calendar.getYearsInEra(date), date.year));
  $735220c2d4774dd3$var$constrainMonthDay(date);
}
function $735220c2d4774dd3$export$3e2544e88a25bff8(duration) {
  let inverseDuration = {};
  for (let key in duration)
    if (typeof duration[key] === "number")
      inverseDuration[key] = -duration[key];
  return inverseDuration;
}
function $735220c2d4774dd3$export$4e2d2ead65e5f7e3(date, duration) {
  return $735220c2d4774dd3$export$e16d8520af44a096(date, $735220c2d4774dd3$export$3e2544e88a25bff8(duration));
}
function $735220c2d4774dd3$export$adaa4cf7ef1b65be(date, fields) {
  let mutableDate = date.copy();
  if (fields.era != null)
    mutableDate.era = fields.era;
  if (fields.year != null)
    mutableDate.year = fields.year;
  if (fields.month != null)
    mutableDate.month = fields.month;
  if (fields.day != null)
    mutableDate.day = fields.day;
  $735220c2d4774dd3$export$c4e2ecac49351ef2(mutableDate);
  return mutableDate;
}
function $735220c2d4774dd3$export$e5d5e1c1822b6e56(value, fields) {
  let mutableValue = value.copy();
  if (fields.hour != null)
    mutableValue.hour = fields.hour;
  if (fields.minute != null)
    mutableValue.minute = fields.minute;
  if (fields.second != null)
    mutableValue.second = fields.second;
  if (fields.millisecond != null)
    mutableValue.millisecond = fields.millisecond;
  $735220c2d4774dd3$export$7555de1e070510cb(mutableValue);
  return mutableValue;
}
function $735220c2d4774dd3$var$balanceTime(time) {
  time.second += Math.floor(time.millisecond / 1e3);
  time.millisecond = $735220c2d4774dd3$var$nonNegativeMod(time.millisecond, 1e3);
  time.minute += Math.floor(time.second / 60);
  time.second = $735220c2d4774dd3$var$nonNegativeMod(time.second, 60);
  time.hour += Math.floor(time.minute / 60);
  time.minute = $735220c2d4774dd3$var$nonNegativeMod(time.minute, 60);
  let days = Math.floor(time.hour / 24);
  time.hour = $735220c2d4774dd3$var$nonNegativeMod(time.hour, 24);
  return days;
}
function $735220c2d4774dd3$export$7555de1e070510cb(time) {
  time.millisecond = Math.max(0, Math.min(time.millisecond, 1e3));
  time.second = Math.max(0, Math.min(time.second, 59));
  time.minute = Math.max(0, Math.min(time.minute, 59));
  time.hour = Math.max(0, Math.min(time.hour, 23));
}
function $735220c2d4774dd3$var$nonNegativeMod(a, b) {
  let result = a % b;
  if (result < 0)
    result += b;
  return result;
}
function $735220c2d4774dd3$var$addTimeFields(time, duration) {
  time.hour += duration.hours || 0;
  time.minute += duration.minutes || 0;
  time.second += duration.seconds || 0;
  time.millisecond += duration.milliseconds || 0;
  return $735220c2d4774dd3$var$balanceTime(time);
}
function $735220c2d4774dd3$export$d52ced6badfb9a4c(value, field, amount, options) {
  let mutable = value.copy();
  switch (field) {
    case "era": {
      let eras = value.calendar.getEras();
      let eraIndex = eras.indexOf(value.era);
      if (eraIndex < 0)
        throw new Error("Invalid era: " + value.era);
      eraIndex = $735220c2d4774dd3$var$cycleValue(eraIndex, amount, 0, eras.length - 1, options === null || options === void 0 ? void 0 : options.round);
      mutable.era = eras[eraIndex];
      $735220c2d4774dd3$export$c4e2ecac49351ef2(mutable);
      break;
    }
    case "year":
      var _mutable_calendar_isInverseEra, _mutable_calendar;
      if ((_mutable_calendar_isInverseEra = (_mutable_calendar = mutable.calendar).isInverseEra) === null || _mutable_calendar_isInverseEra === void 0 ? void 0 : _mutable_calendar_isInverseEra.call(_mutable_calendar, mutable))
        amount = -amount;
      mutable.year = $735220c2d4774dd3$var$cycleValue(value.year, amount, -Infinity, 9999, options === null || options === void 0 ? void 0 : options.round);
      if (mutable.year === -Infinity)
        mutable.year = 1;
      if (mutable.calendar.balanceYearMonth)
        mutable.calendar.balanceYearMonth(mutable, value);
      break;
    case "month":
      mutable.month = $735220c2d4774dd3$var$cycleValue(value.month, amount, 1, value.calendar.getMonthsInYear(value), options === null || options === void 0 ? void 0 : options.round);
      break;
    case "day":
      mutable.day = $735220c2d4774dd3$var$cycleValue(value.day, amount, 1, value.calendar.getDaysInMonth(value), options === null || options === void 0 ? void 0 : options.round);
      break;
    default:
      throw new Error("Unsupported field " + field);
  }
  if (value.calendar.balanceDate)
    value.calendar.balanceDate(mutable);
  $735220c2d4774dd3$export$c4e2ecac49351ef2(mutable);
  return mutable;
}
function $735220c2d4774dd3$export$dd02b3e0007dfe28(value, field, amount, options) {
  let mutable = value.copy();
  switch (field) {
    case "hour": {
      let hours = value.hour;
      let min = 0;
      let max = 23;
      if ((options === null || options === void 0 ? void 0 : options.hourCycle) === 12) {
        let isPM = hours >= 12;
        min = isPM ? 12 : 0;
        max = isPM ? 23 : 11;
      }
      mutable.hour = $735220c2d4774dd3$var$cycleValue(hours, amount, min, max, options === null || options === void 0 ? void 0 : options.round);
      break;
    }
    case "minute":
      mutable.minute = $735220c2d4774dd3$var$cycleValue(value.minute, amount, 0, 59, options === null || options === void 0 ? void 0 : options.round);
      break;
    case "second":
      mutable.second = $735220c2d4774dd3$var$cycleValue(value.second, amount, 0, 59, options === null || options === void 0 ? void 0 : options.round);
      break;
    case "millisecond":
      mutable.millisecond = $735220c2d4774dd3$var$cycleValue(value.millisecond, amount, 0, 999, options === null || options === void 0 ? void 0 : options.round);
      break;
    default:
      throw new Error("Unsupported field " + field);
  }
  return mutable;
}
function $735220c2d4774dd3$var$cycleValue(value, amount, min, max, round = false) {
  if (round) {
    value += Math.sign(amount);
    if (value < min)
      value = max;
    let div = Math.abs(amount);
    if (amount > 0)
      value = Math.ceil(value / div) * div;
    else
      value = Math.floor(value / div) * div;
    if (value > max)
      value = min;
  } else {
    value += amount;
    if (value < min)
      value = max - (min - value - 1);
    else if (value > max)
      value = min + (value - max - 1);
  }
  return value;
}
function $735220c2d4774dd3$export$96b1d28349274637(dateTime, duration) {
  let ms;
  if (duration.years != null && duration.years !== 0 || duration.months != null && duration.months !== 0 || duration.weeks != null && duration.weeks !== 0 || duration.days != null && duration.days !== 0) {
    let res2 = $735220c2d4774dd3$export$e16d8520af44a096(($11d87f3f76e88657$export$b21e0b124e224484)(dateTime), {
      years: duration.years,
      months: duration.months,
      weeks: duration.weeks,
      days: duration.days
    });
    ms = ($11d87f3f76e88657$export$5107c82f94518f5c)(res2, dateTime.timeZone);
  } else
    ms = ($11d87f3f76e88657$export$bd4fb2bc8bb06fb)(dateTime) - dateTime.offset;
  ms += duration.milliseconds || 0;
  ms += (duration.seconds || 0) * 1e3;
  ms += (duration.minutes || 0) * 6e4;
  ms += (duration.hours || 0) * 36e5;
  let res = ($11d87f3f76e88657$export$1b96692a1ba042ac)(ms, dateTime.timeZone);
  return ($11d87f3f76e88657$export$b4a036af3fc0b032)(res, dateTime.calendar);
}
function $735220c2d4774dd3$export$6814caac34ca03c7(dateTime, duration) {
  return $735220c2d4774dd3$export$96b1d28349274637(dateTime, $735220c2d4774dd3$export$3e2544e88a25bff8(duration));
}
function $735220c2d4774dd3$export$9a297d111fc86b79(dateTime, field, amount, options) {
  switch (field) {
    case "hour": {
      let min = 0;
      let max = 23;
      if ((options === null || options === void 0 ? void 0 : options.hourCycle) === 12) {
        let isPM = dateTime.hour >= 12;
        min = isPM ? 12 : 0;
        max = isPM ? 23 : 11;
      }
      let plainDateTime = ($11d87f3f76e88657$export$b21e0b124e224484)(dateTime);
      let minDate = ($11d87f3f76e88657$export$b4a036af3fc0b032)($735220c2d4774dd3$export$e5d5e1c1822b6e56(plainDateTime, {
        hour: min
      }), new ($3b62074eb05584b2$export$80ee6245ec4f29ec)());
      let minAbsolute = [
        ($11d87f3f76e88657$export$5107c82f94518f5c)(minDate, dateTime.timeZone, "earlier"),
        ($11d87f3f76e88657$export$5107c82f94518f5c)(minDate, dateTime.timeZone, "later")
      ].filter((ms2) => ($11d87f3f76e88657$export$1b96692a1ba042ac)(ms2, dateTime.timeZone).day === minDate.day)[0];
      let maxDate = ($11d87f3f76e88657$export$b4a036af3fc0b032)($735220c2d4774dd3$export$e5d5e1c1822b6e56(plainDateTime, {
        hour: max
      }), new ($3b62074eb05584b2$export$80ee6245ec4f29ec)());
      let maxAbsolute = [
        ($11d87f3f76e88657$export$5107c82f94518f5c)(maxDate, dateTime.timeZone, "earlier"),
        ($11d87f3f76e88657$export$5107c82f94518f5c)(maxDate, dateTime.timeZone, "later")
      ].filter((ms2) => ($11d87f3f76e88657$export$1b96692a1ba042ac)(ms2, dateTime.timeZone).day === maxDate.day).pop();
      let ms = ($11d87f3f76e88657$export$bd4fb2bc8bb06fb)(dateTime) - dateTime.offset;
      let hours = Math.floor(ms / $735220c2d4774dd3$var$ONE_HOUR);
      let remainder = ms % $735220c2d4774dd3$var$ONE_HOUR;
      ms = $735220c2d4774dd3$var$cycleValue(hours, amount, Math.floor(minAbsolute / $735220c2d4774dd3$var$ONE_HOUR), Math.floor(maxAbsolute / $735220c2d4774dd3$var$ONE_HOUR), options === null || options === void 0 ? void 0 : options.round) * $735220c2d4774dd3$var$ONE_HOUR + remainder;
      return ($11d87f3f76e88657$export$b4a036af3fc0b032)(($11d87f3f76e88657$export$1b96692a1ba042ac)(ms, dateTime.timeZone), dateTime.calendar);
    }
    case "minute":
    case "second":
    case "millisecond":
      return $735220c2d4774dd3$export$dd02b3e0007dfe28(dateTime, field, amount, options);
    case "era":
    case "year":
    case "month":
    case "day": {
      let res = $735220c2d4774dd3$export$d52ced6badfb9a4c(($11d87f3f76e88657$export$b21e0b124e224484)(dateTime), field, amount, options);
      let ms = ($11d87f3f76e88657$export$5107c82f94518f5c)(res, dateTime.timeZone);
      return ($11d87f3f76e88657$export$b4a036af3fc0b032)(($11d87f3f76e88657$export$1b96692a1ba042ac)(ms, dateTime.timeZone), dateTime.calendar);
    }
    default:
      throw new Error("Unsupported field " + field);
  }
}
function $735220c2d4774dd3$export$31b5430eb18be4f8(dateTime, fields, disambiguation) {
  let plainDateTime = ($11d87f3f76e88657$export$b21e0b124e224484)(dateTime);
  let res = $735220c2d4774dd3$export$e5d5e1c1822b6e56($735220c2d4774dd3$export$adaa4cf7ef1b65be(plainDateTime, fields), fields);
  if (res.compare(plainDateTime) === 0)
    return dateTime;
  let ms = ($11d87f3f76e88657$export$5107c82f94518f5c)(res, dateTime.timeZone, disambiguation);
  return ($11d87f3f76e88657$export$b4a036af3fc0b032)(($11d87f3f76e88657$export$1b96692a1ba042ac)(ms, dateTime.timeZone), dateTime.calendar);
}
function $fae977aafc393c5c$export$f59dee82248f5ad4(time) {
  return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}:${String(time.second).padStart(2, "0")}${time.millisecond ? String(time.millisecond / 1e3).slice(1) : ""}`;
}
function $fae977aafc393c5c$export$60dfd74aa96791bd(date) {
  let gregorianDate = ($11d87f3f76e88657$export$b4a036af3fc0b032)(date, new ($3b62074eb05584b2$export$80ee6245ec4f29ec)());
  let year;
  if (gregorianDate.era === "BC")
    year = gregorianDate.year === 1 ? "0000" : "-" + String(Math.abs(1 - gregorianDate.year)).padStart(6, "00");
  else
    year = String(gregorianDate.year).padStart(4, "0");
  return `${year}-${String(gregorianDate.month).padStart(2, "0")}-${String(gregorianDate.day).padStart(2, "0")}`;
}
function $fae977aafc393c5c$export$4223de14708adc63(date) {
  return `${$fae977aafc393c5c$export$60dfd74aa96791bd(date)}T${$fae977aafc393c5c$export$f59dee82248f5ad4(date)}`;
}
function $fae977aafc393c5c$var$offsetToString(offset) {
  let sign = Math.sign(offset) < 0 ? "-" : "+";
  offset = Math.abs(offset);
  let offsetHours = Math.floor(offset / 36e5);
  let offsetMinutes = offset % 36e5 / 6e4;
  return `${sign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutes).padStart(2, "0")}`;
}
function $fae977aafc393c5c$export$bf79f1ebf4b18792(date) {
  return `${$fae977aafc393c5c$export$4223de14708adc63(date)}${$fae977aafc393c5c$var$offsetToString(date.offset)}[${date.timeZone}]`;
}

// node_modules/@swc/helpers/esm/_check_private_redeclaration.js
function _check_private_redeclaration(obj, privateCollection) {
  if (privateCollection.has(obj)) {
    throw new TypeError("Cannot initialize the same private elements twice on an object");
  }
}

// node_modules/@swc/helpers/esm/_class_private_field_init.js
function _class_private_field_init(obj, privateMap, value) {
  _check_private_redeclaration(obj, privateMap);
  privateMap.set(obj, value);
}

// node_modules/@internationalized/date/dist/CalendarDate.mjs
function $35ea8db9cb2ccb90$var$shiftArgs(args) {
  let calendar = typeof args[0] === "object" ? args.shift() : new ($3b62074eb05584b2$export$80ee6245ec4f29ec)();
  let era;
  if (typeof args[0] === "string")
    era = args.shift();
  else {
    let eras = calendar.getEras();
    era = eras[eras.length - 1];
  }
  let year = args.shift();
  let month = args.shift();
  let day = args.shift();
  return [
    calendar,
    era,
    year,
    month,
    day
  ];
}
var $35ea8db9cb2ccb90$var$_type = /* @__PURE__ */ new WeakMap();
var $35ea8db9cb2ccb90$export$99faa760c7908e4f = class _$35ea8db9cb2ccb90$export$99faa760c7908e4f {
  /** Returns a copy of this date. */
  copy() {
    if (this.era)
      return new _$35ea8db9cb2ccb90$export$99faa760c7908e4f(this.calendar, this.era, this.year, this.month, this.day);
    else
      return new _$35ea8db9cb2ccb90$export$99faa760c7908e4f(this.calendar, this.year, this.month, this.day);
  }
  /** Returns a new `CalendarDate` with the given duration added to it. */
  add(duration) {
    return ($735220c2d4774dd3$export$e16d8520af44a096)(this, duration);
  }
  /** Returns a new `CalendarDate` with the given duration subtracted from it. */
  subtract(duration) {
    return ($735220c2d4774dd3$export$4e2d2ead65e5f7e3)(this, duration);
  }
  /** Returns a new `CalendarDate` with the given fields set to the provided values. Other fields will be constrained accordingly. */
  set(fields) {
    return ($735220c2d4774dd3$export$adaa4cf7ef1b65be)(this, fields);
  }
  /**
  * Returns a new `CalendarDate` with the given field adjusted by a specified amount.
  * When the resulting value reaches the limits of the field, it wraps around.
  */
  cycle(field, amount, options) {
    return ($735220c2d4774dd3$export$d52ced6badfb9a4c)(this, field, amount, options);
  }
  /** Converts the date to a native JavaScript Date object, with the time set to midnight in the given time zone. */
  toDate(timeZone) {
    return ($11d87f3f76e88657$export$e67a095c620b86fe)(this, timeZone);
  }
  /** Converts the date to an ISO 8601 formatted string. */
  toString() {
    return ($fae977aafc393c5c$export$60dfd74aa96791bd)(this);
  }
  /** Compares this date with another. A negative result indicates that this date is before the given one, and a positive date indicates that it is after. */
  compare(b) {
    return ($14e0f24ef4ac5c92$export$68781ddf31c0090f)(this, b);
  }
  constructor(...args) {
    (_class_private_field_init)(this, $35ea8db9cb2ccb90$var$_type, {
      writable: true,
      value: void 0
    });
    let [calendar, era, year, month, day] = $35ea8db9cb2ccb90$var$shiftArgs(args);
    this.calendar = calendar;
    this.era = era;
    this.year = year;
    this.month = month;
    this.day = day;
    ($735220c2d4774dd3$export$c4e2ecac49351ef2)(this);
  }
};
var $35ea8db9cb2ccb90$var$_type2 = /* @__PURE__ */ new WeakMap();
var $35ea8db9cb2ccb90$export$ca871e8dbb80966f = class _$35ea8db9cb2ccb90$export$ca871e8dbb80966f {
  /** Returns a copy of this date. */
  copy() {
    if (this.era)
      return new _$35ea8db9cb2ccb90$export$ca871e8dbb80966f(this.calendar, this.era, this.year, this.month, this.day, this.hour, this.minute, this.second, this.millisecond);
    else
      return new _$35ea8db9cb2ccb90$export$ca871e8dbb80966f(this.calendar, this.year, this.month, this.day, this.hour, this.minute, this.second, this.millisecond);
  }
  /** Returns a new `CalendarDateTime` with the given duration added to it. */
  add(duration) {
    return ($735220c2d4774dd3$export$e16d8520af44a096)(this, duration);
  }
  /** Returns a new `CalendarDateTime` with the given duration subtracted from it. */
  subtract(duration) {
    return ($735220c2d4774dd3$export$4e2d2ead65e5f7e3)(this, duration);
  }
  /** Returns a new `CalendarDateTime` with the given fields set to the provided values. Other fields will be constrained accordingly. */
  set(fields) {
    return ($735220c2d4774dd3$export$adaa4cf7ef1b65be)(($735220c2d4774dd3$export$e5d5e1c1822b6e56)(this, fields), fields);
  }
  /**
  * Returns a new `CalendarDateTime` with the given field adjusted by a specified amount.
  * When the resulting value reaches the limits of the field, it wraps around.
  */
  cycle(field, amount, options) {
    switch (field) {
      case "era":
      case "year":
      case "month":
      case "day":
        return ($735220c2d4774dd3$export$d52ced6badfb9a4c)(this, field, amount, options);
      default:
        return ($735220c2d4774dd3$export$dd02b3e0007dfe28)(this, field, amount, options);
    }
  }
  /** Converts the date to a native JavaScript Date object in the given time zone. */
  toDate(timeZone, disambiguation) {
    return ($11d87f3f76e88657$export$e67a095c620b86fe)(this, timeZone, disambiguation);
  }
  /** Converts the date to an ISO 8601 formatted string. */
  toString() {
    return ($fae977aafc393c5c$export$4223de14708adc63)(this);
  }
  /** Compares this date with another. A negative result indicates that this date is before the given one, and a positive date indicates that it is after. */
  compare(b) {
    let res = ($14e0f24ef4ac5c92$export$68781ddf31c0090f)(this, b);
    if (res === 0)
      return ($14e0f24ef4ac5c92$export$c19a80a9721b80f6)(this, ($11d87f3f76e88657$export$b21e0b124e224484)(b));
    return res;
  }
  constructor(...args) {
    (_class_private_field_init)(this, $35ea8db9cb2ccb90$var$_type2, {
      writable: true,
      value: void 0
    });
    let [calendar, era, year, month, day] = $35ea8db9cb2ccb90$var$shiftArgs(args);
    this.calendar = calendar;
    this.era = era;
    this.year = year;
    this.month = month;
    this.day = day;
    this.hour = args.shift() || 0;
    this.minute = args.shift() || 0;
    this.second = args.shift() || 0;
    this.millisecond = args.shift() || 0;
    ($735220c2d4774dd3$export$c4e2ecac49351ef2)(this);
  }
};
var $35ea8db9cb2ccb90$var$_type3 = /* @__PURE__ */ new WeakMap();
var $35ea8db9cb2ccb90$export$d3b7288e7994edea = class _$35ea8db9cb2ccb90$export$d3b7288e7994edea {
  /** Returns a copy of this date. */
  copy() {
    if (this.era)
      return new _$35ea8db9cb2ccb90$export$d3b7288e7994edea(this.calendar, this.era, this.year, this.month, this.day, this.timeZone, this.offset, this.hour, this.minute, this.second, this.millisecond);
    else
      return new _$35ea8db9cb2ccb90$export$d3b7288e7994edea(this.calendar, this.year, this.month, this.day, this.timeZone, this.offset, this.hour, this.minute, this.second, this.millisecond);
  }
  /** Returns a new `ZonedDateTime` with the given duration added to it. */
  add(duration) {
    return ($735220c2d4774dd3$export$96b1d28349274637)(this, duration);
  }
  /** Returns a new `ZonedDateTime` with the given duration subtracted from it. */
  subtract(duration) {
    return ($735220c2d4774dd3$export$6814caac34ca03c7)(this, duration);
  }
  /** Returns a new `ZonedDateTime` with the given fields set to the provided values. Other fields will be constrained accordingly. */
  set(fields, disambiguation) {
    return ($735220c2d4774dd3$export$31b5430eb18be4f8)(this, fields, disambiguation);
  }
  /**
  * Returns a new `ZonedDateTime` with the given field adjusted by a specified amount.
  * When the resulting value reaches the limits of the field, it wraps around.
  */
  cycle(field, amount, options) {
    return ($735220c2d4774dd3$export$9a297d111fc86b79)(this, field, amount, options);
  }
  /** Converts the date to a native JavaScript Date object. */
  toDate() {
    return ($11d87f3f76e88657$export$83aac07b4c37b25)(this);
  }
  /** Converts the date to an ISO 8601 formatted string, including the UTC offset and time zone identifier. */
  toString() {
    return ($fae977aafc393c5c$export$bf79f1ebf4b18792)(this);
  }
  /** Converts the date to an ISO 8601 formatted string in UTC. */
  toAbsoluteString() {
    return this.toDate().toISOString();
  }
  /** Compares this date with another. A negative result indicates that this date is before the given one, and a positive date indicates that it is after. */
  compare(b) {
    return this.toDate().getTime() - ($11d87f3f76e88657$export$84c95a83c799e074)(b, this.timeZone).toDate().getTime();
  }
  constructor(...args) {
    (_class_private_field_init)(this, $35ea8db9cb2ccb90$var$_type3, {
      writable: true,
      value: void 0
    });
    let [calendar, era, year, month, day] = $35ea8db9cb2ccb90$var$shiftArgs(args);
    let timeZone = args.shift();
    let offset = args.shift();
    this.calendar = calendar;
    this.era = era;
    this.year = year;
    this.month = month;
    this.day = day;
    this.timeZone = timeZone;
    this.offset = offset;
    this.hour = args.shift() || 0;
    this.minute = args.shift() || 0;
    this.second = args.shift() || 0;
    this.millisecond = args.shift() || 0;
    ($735220c2d4774dd3$export$c4e2ecac49351ef2)(this);
  }
};

// node_modules/@internationalized/date/dist/JapaneseCalendar.mjs
var $62225008020f0a13$var$ERA_START_DATES = [
  [
    1868,
    9,
    8
  ],
  [
    1912,
    7,
    30
  ],
  [
    1926,
    12,
    25
  ],
  [
    1989,
    1,
    8
  ],
  [
    2019,
    5,
    1
  ]
];
var $62225008020f0a13$var$ERA_END_DATES = [
  [
    1912,
    7,
    29
  ],
  [
    1926,
    12,
    24
  ],
  [
    1989,
    1,
    7
  ],
  [
    2019,
    4,
    30
  ]
];
var $62225008020f0a13$var$ERA_ADDENDS = [
  1867,
  1911,
  1925,
  1988,
  2018
];
var $62225008020f0a13$var$ERA_NAMES = [
  "meiji",
  "taisho",
  "showa",
  "heisei",
  "reiwa"
];
function $62225008020f0a13$var$findEraFromGregorianDate(date) {
  const idx = $62225008020f0a13$var$ERA_START_DATES.findIndex(([year, month, day]) => {
    if (date.year < year)
      return true;
    if (date.year === year && date.month < month)
      return true;
    if (date.year === year && date.month === month && date.day < day)
      return true;
    return false;
  });
  if (idx === -1)
    return $62225008020f0a13$var$ERA_START_DATES.length - 1;
  if (idx === 0)
    return 0;
  return idx - 1;
}
function $62225008020f0a13$var$toGregorian(date) {
  let eraAddend = $62225008020f0a13$var$ERA_ADDENDS[$62225008020f0a13$var$ERA_NAMES.indexOf(date.era)];
  if (!eraAddend)
    throw new Error("Unknown era: " + date.era);
  return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(date.year + eraAddend, date.month, date.day);
}
var $62225008020f0a13$export$b746ab2b60cdffbf = class extends ($3b62074eb05584b2$export$80ee6245ec4f29ec) {
  fromJulianDay(jd) {
    let date = super.fromJulianDay(jd);
    let era = $62225008020f0a13$var$findEraFromGregorianDate(date);
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, $62225008020f0a13$var$ERA_NAMES[era], date.year - $62225008020f0a13$var$ERA_ADDENDS[era], date.month, date.day);
  }
  toJulianDay(date) {
    return super.toJulianDay($62225008020f0a13$var$toGregorian(date));
  }
  balanceDate(date) {
    let gregorianDate = $62225008020f0a13$var$toGregorian(date);
    let era = $62225008020f0a13$var$findEraFromGregorianDate(gregorianDate);
    if ($62225008020f0a13$var$ERA_NAMES[era] !== date.era) {
      date.era = $62225008020f0a13$var$ERA_NAMES[era];
      date.year = gregorianDate.year - $62225008020f0a13$var$ERA_ADDENDS[era];
    }
    this.constrainDate(date);
  }
  constrainDate(date) {
    let idx = $62225008020f0a13$var$ERA_NAMES.indexOf(date.era);
    let end = $62225008020f0a13$var$ERA_END_DATES[idx];
    if (end != null) {
      let [endYear, endMonth, endDay] = end;
      let maxYear = endYear - $62225008020f0a13$var$ERA_ADDENDS[idx];
      date.year = Math.max(1, Math.min(maxYear, date.year));
      if (date.year === maxYear) {
        date.month = Math.min(endMonth, date.month);
        if (date.month === endMonth)
          date.day = Math.min(endDay, date.day);
      }
    }
    if (date.year === 1 && idx >= 0) {
      let [, startMonth, startDay] = $62225008020f0a13$var$ERA_START_DATES[idx];
      date.month = Math.max(startMonth, date.month);
      if (date.month === startMonth)
        date.day = Math.max(startDay, date.day);
    }
  }
  getEras() {
    return $62225008020f0a13$var$ERA_NAMES;
  }
  getYearsInEra(date) {
    let era = $62225008020f0a13$var$ERA_NAMES.indexOf(date.era);
    let cur = $62225008020f0a13$var$ERA_START_DATES[era];
    let next = $62225008020f0a13$var$ERA_START_DATES[era + 1];
    if (next == null)
      return 9999 - cur[0] + 1;
    let years = next[0] - cur[0];
    if (date.month < next[1] || date.month === next[1] && date.day < next[2])
      years++;
    return years;
  }
  getDaysInMonth(date) {
    return super.getDaysInMonth($62225008020f0a13$var$toGregorian(date));
  }
  getMinimumMonthInYear(date) {
    let start = $62225008020f0a13$var$getMinimums(date);
    return start ? start[1] : 1;
  }
  getMinimumDayInMonth(date) {
    let start = $62225008020f0a13$var$getMinimums(date);
    return start && date.month === start[1] ? start[2] : 1;
  }
  constructor(...args) {
    super(...args), this.identifier = "japanese";
  }
};
function $62225008020f0a13$var$getMinimums(date) {
  if (date.year === 1) {
    let idx = $62225008020f0a13$var$ERA_NAMES.indexOf(date.era);
    return $62225008020f0a13$var$ERA_START_DATES[idx];
  }
}

// node_modules/@internationalized/date/dist/BuddhistCalendar.mjs
var $8d73d47422ca7302$var$BUDDHIST_ERA_START = -543;
var $8d73d47422ca7302$export$42d20a78301dee44 = class extends ($3b62074eb05584b2$export$80ee6245ec4f29ec) {
  fromJulianDay(jd) {
    let gregorianDate = super.fromJulianDay(jd);
    let year = ($3b62074eb05584b2$export$c36e0ecb2d4fa69d)(gregorianDate.era, gregorianDate.year);
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, year - $8d73d47422ca7302$var$BUDDHIST_ERA_START, gregorianDate.month, gregorianDate.day);
  }
  toJulianDay(date) {
    return super.toJulianDay($8d73d47422ca7302$var$toGregorian(date));
  }
  getEras() {
    return [
      "BE"
    ];
  }
  getDaysInMonth(date) {
    return super.getDaysInMonth($8d73d47422ca7302$var$toGregorian(date));
  }
  balanceDate() {
  }
  constructor(...args) {
    super(...args), this.identifier = "buddhist";
  }
};
function $8d73d47422ca7302$var$toGregorian(date) {
  let [era, year] = ($3b62074eb05584b2$export$4475b7e617eb123c)(date.year + $8d73d47422ca7302$var$BUDDHIST_ERA_START);
  return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(era, year, date.month, date.day);
}

// node_modules/@internationalized/date/dist/TaiwanCalendar.mjs
var $5f31bd6f0c8940b2$var$TAIWAN_ERA_START = 1911;
function $5f31bd6f0c8940b2$var$gregorianYear(date) {
  return date.era === "minguo" ? date.year + $5f31bd6f0c8940b2$var$TAIWAN_ERA_START : 1 - date.year + $5f31bd6f0c8940b2$var$TAIWAN_ERA_START;
}
function $5f31bd6f0c8940b2$var$gregorianToTaiwan(year) {
  let y = year - $5f31bd6f0c8940b2$var$TAIWAN_ERA_START;
  if (y > 0)
    return [
      "minguo",
      y
    ];
  else
    return [
      "before_minguo",
      1 - y
    ];
}
var $5f31bd6f0c8940b2$export$65e01080afcb0799 = class extends ($3b62074eb05584b2$export$80ee6245ec4f29ec) {
  fromJulianDay(jd) {
    let date = super.fromJulianDay(jd);
    let extendedYear = ($3b62074eb05584b2$export$c36e0ecb2d4fa69d)(date.era, date.year);
    let [era, year] = $5f31bd6f0c8940b2$var$gregorianToTaiwan(extendedYear);
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, era, year, date.month, date.day);
  }
  toJulianDay(date) {
    return super.toJulianDay($5f31bd6f0c8940b2$var$toGregorian(date));
  }
  getEras() {
    return [
      "before_minguo",
      "minguo"
    ];
  }
  balanceDate(date) {
    let [era, year] = $5f31bd6f0c8940b2$var$gregorianToTaiwan($5f31bd6f0c8940b2$var$gregorianYear(date));
    date.era = era;
    date.year = year;
  }
  isInverseEra(date) {
    return date.era === "before_minguo";
  }
  getDaysInMonth(date) {
    return super.getDaysInMonth($5f31bd6f0c8940b2$var$toGregorian(date));
  }
  getYearsInEra(date) {
    return date.era === "before_minguo" ? 9999 : 9999 - $5f31bd6f0c8940b2$var$TAIWAN_ERA_START;
  }
  constructor(...args) {
    super(...args), this.identifier = "roc";
  }
};
function $5f31bd6f0c8940b2$var$toGregorian(date) {
  let [era, year] = ($3b62074eb05584b2$export$4475b7e617eb123c)($5f31bd6f0c8940b2$var$gregorianYear(date));
  return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(era, year, date.month, date.day);
}

// node_modules/@internationalized/date/dist/PersianCalendar.mjs
var $f3ed2e4472ae7e25$var$PERSIAN_EPOCH = 1948320;
var $f3ed2e4472ae7e25$var$MONTH_START = [
  0,
  31,
  62,
  93,
  124,
  155,
  186,
  216,
  246,
  276,
  306,
  336
  // Esfand
];
var $f3ed2e4472ae7e25$export$37fccdbfd14c5939 = class {
  fromJulianDay(jd) {
    let daysSinceEpoch = jd - $f3ed2e4472ae7e25$var$PERSIAN_EPOCH;
    let year = 1 + Math.floor((33 * daysSinceEpoch + 3) / 12053);
    let farvardin1 = 365 * (year - 1) + Math.floor((8 * year + 21) / 33);
    let dayOfYear = daysSinceEpoch - farvardin1;
    let month = dayOfYear < 216 ? Math.floor(dayOfYear / 31) : Math.floor((dayOfYear - 6) / 30);
    let day = dayOfYear - $f3ed2e4472ae7e25$var$MONTH_START[month] + 1;
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, year, month + 1, day);
  }
  toJulianDay(date) {
    let jd = $f3ed2e4472ae7e25$var$PERSIAN_EPOCH - 1 + 365 * (date.year - 1) + Math.floor((8 * date.year + 21) / 33);
    jd += $f3ed2e4472ae7e25$var$MONTH_START[date.month - 1];
    jd += date.day;
    return jd;
  }
  getMonthsInYear() {
    return 12;
  }
  getDaysInMonth(date) {
    if (date.month <= 6)
      return 31;
    if (date.month <= 11)
      return 30;
    let isLeapYear = ($2b4dce13dd5a17fa$export$842a2cf37af977e1)(25 * date.year + 11, 33) < 8;
    return isLeapYear ? 30 : 29;
  }
  getEras() {
    return [
      "AP"
    ];
  }
  getYearsInEra() {
    return 9377;
  }
  constructor() {
    this.identifier = "persian";
  }
};

// node_modules/@internationalized/date/dist/IndianCalendar.mjs
var $82c358003bdda0a8$var$INDIAN_ERA_START = 78;
var $82c358003bdda0a8$var$INDIAN_YEAR_START = 80;
var $82c358003bdda0a8$export$39f31c639fa15726 = class extends ($3b62074eb05584b2$export$80ee6245ec4f29ec) {
  fromJulianDay(jd) {
    let date = super.fromJulianDay(jd);
    let indianYear = date.year - $82c358003bdda0a8$var$INDIAN_ERA_START;
    let yDay = jd - ($3b62074eb05584b2$export$f297eb839006d339)(date.era, date.year, 1, 1);
    let leapMonth;
    if (yDay < $82c358003bdda0a8$var$INDIAN_YEAR_START) {
      indianYear--;
      leapMonth = ($3b62074eb05584b2$export$553d7fa8e3805fc0)(date.year - 1) ? 31 : 30;
      yDay += leapMonth + 155 + 90 + 10;
    } else {
      leapMonth = ($3b62074eb05584b2$export$553d7fa8e3805fc0)(date.year) ? 31 : 30;
      yDay -= $82c358003bdda0a8$var$INDIAN_YEAR_START;
    }
    let indianMonth;
    let indianDay;
    if (yDay < leapMonth) {
      indianMonth = 1;
      indianDay = yDay + 1;
    } else {
      let mDay = yDay - leapMonth;
      if (mDay < 155) {
        indianMonth = Math.floor(mDay / 31) + 2;
        indianDay = mDay % 31 + 1;
      } else {
        mDay -= 155;
        indianMonth = Math.floor(mDay / 30) + 7;
        indianDay = mDay % 30 + 1;
      }
    }
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, indianYear, indianMonth, indianDay);
  }
  toJulianDay(date) {
    let extendedYear = date.year + $82c358003bdda0a8$var$INDIAN_ERA_START;
    let [era, year] = ($3b62074eb05584b2$export$4475b7e617eb123c)(extendedYear);
    let leapMonth;
    let jd;
    if (($3b62074eb05584b2$export$553d7fa8e3805fc0)(year)) {
      leapMonth = 31;
      jd = ($3b62074eb05584b2$export$f297eb839006d339)(era, year, 3, 21);
    } else {
      leapMonth = 30;
      jd = ($3b62074eb05584b2$export$f297eb839006d339)(era, year, 3, 22);
    }
    if (date.month === 1)
      return jd + date.day - 1;
    jd += leapMonth + Math.min(date.month - 2, 5) * 31;
    if (date.month >= 8)
      jd += (date.month - 7) * 30;
    jd += date.day - 1;
    return jd;
  }
  getDaysInMonth(date) {
    if (date.month === 1 && ($3b62074eb05584b2$export$553d7fa8e3805fc0)(date.year + $82c358003bdda0a8$var$INDIAN_ERA_START))
      return 31;
    if (date.month >= 2 && date.month <= 6)
      return 31;
    return 30;
  }
  getYearsInEra() {
    return 9919;
  }
  getEras() {
    return [
      "saka"
    ];
  }
  balanceDate() {
  }
  constructor(...args) {
    super(...args), this.identifier = "indian";
  }
};

// node_modules/@internationalized/date/dist/IslamicCalendar.mjs
var $f2f3e0e3a817edbd$var$CIVIL_EPOC = 1948440;
var $f2f3e0e3a817edbd$var$ASTRONOMICAL_EPOC = 1948439;
var $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START = 1300;
var $f2f3e0e3a817edbd$var$UMALQURA_YEAR_END = 1600;
var $f2f3e0e3a817edbd$var$UMALQURA_START_DAYS = 460322;
function $f2f3e0e3a817edbd$var$islamicToJulianDay(epoch, year, month, day) {
  return day + Math.ceil(29.5 * (month - 1)) + (year - 1) * 354 + Math.floor((3 + 11 * year) / 30) + epoch - 1;
}
function $f2f3e0e3a817edbd$var$julianDayToIslamic(calendar, epoch, jd) {
  let year = Math.floor((30 * (jd - epoch) + 10646) / 10631);
  let month = Math.min(12, Math.ceil((jd - (29 + $f2f3e0e3a817edbd$var$islamicToJulianDay(epoch, year, 1, 1))) / 29.5) + 1);
  let day = jd - $f2f3e0e3a817edbd$var$islamicToJulianDay(epoch, year, month, 1) + 1;
  return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(calendar, year, month, day);
}
function $f2f3e0e3a817edbd$var$isLeapYear(year) {
  return (14 + 11 * year) % 30 < 11;
}
var $f2f3e0e3a817edbd$export$2066795aadd37bfc = class {
  fromJulianDay(jd) {
    return $f2f3e0e3a817edbd$var$julianDayToIslamic(this, $f2f3e0e3a817edbd$var$CIVIL_EPOC, jd);
  }
  toJulianDay(date) {
    return $f2f3e0e3a817edbd$var$islamicToJulianDay($f2f3e0e3a817edbd$var$CIVIL_EPOC, date.year, date.month, date.day);
  }
  getDaysInMonth(date) {
    let length = 29 + date.month % 2;
    if (date.month === 12 && $f2f3e0e3a817edbd$var$isLeapYear(date.year))
      length++;
    return length;
  }
  getMonthsInYear() {
    return 12;
  }
  getDaysInYear(date) {
    return $f2f3e0e3a817edbd$var$isLeapYear(date.year) ? 355 : 354;
  }
  getYearsInEra() {
    return 9665;
  }
  getEras() {
    return [
      "AH"
    ];
  }
  constructor() {
    this.identifier = "islamic-civil";
  }
};
var $f2f3e0e3a817edbd$export$37f0887f2f9d22f7 = class extends $f2f3e0e3a817edbd$export$2066795aadd37bfc {
  fromJulianDay(jd) {
    return $f2f3e0e3a817edbd$var$julianDayToIslamic(this, $f2f3e0e3a817edbd$var$ASTRONOMICAL_EPOC, jd);
  }
  toJulianDay(date) {
    return $f2f3e0e3a817edbd$var$islamicToJulianDay($f2f3e0e3a817edbd$var$ASTRONOMICAL_EPOC, date.year, date.month, date.day);
  }
  constructor(...args) {
    super(...args), this.identifier = "islamic-tbla";
  }
};
var $f2f3e0e3a817edbd$var$UMALQURA_DATA = "qgpUDckO1AbqBmwDrQpVBakGkgepC9QF2gpcBS0NlQZKB1QLagutBa4ETwoXBYsGpQbVCtYCWwmdBE0KJg2VDawFtgm6AlsKKwWVCsoG6Qr0AnYJtgJWCcoKpAvSC9kF3AJtCU0FpQpSC6ULtAW2CVcFlwJLBaMGUgdlC2oFqworBZUMSg2lDcoF1gpXCasESwmlClILagt1BXYCtwhbBFUFqQW0BdoJ3QRuAjYJqgpUDbIN1QXaAlsJqwRVCkkLZAtxC7QFtQpVCiUNkg7JDtQG6QprCasEkwpJDaQNsg25CroEWworBZUKKgtVC1wFvQQ9Ah0JlQpKC1oLbQW2AjsJmwRVBqkGVAdqC2wFrQpVBSkLkgupC9QF2gpaBasKlQVJB2QHqgu1BbYCVgpNDiULUgtqC60FrgIvCZcESwalBqwG1gpdBZ0ETQoWDZUNqgW1BdoCWwmtBJUFygbkBuoK9QS2AlYJqgpUC9IL2QXqAm0JrQSVCkoLpQuyBbUJ1gSXCkcFkwZJB1ULagVrCisFiwpGDaMNygXWCtsEawJLCaUKUgtpC3UFdgG3CFsCKwVlBbQF2gntBG0BtgimClINqQ3UBdoKWwmrBFMGKQdiB6kLsgW1ClUFJQuSDckO0gbpCmsFqwRVCikNVA2qDbUJugQ7CpsETQqqCtUK2gJdCV4ELgqaDFUNsga5BroEXQotBZUKUguoC7QLuQXaAloJSgukDdEO6AZqC20FNQWVBkoNqA3UDdoGWwWdAisGFQtKC5ULqgWuCi4JjwwnBZUGqgbWCl0FnQI=";
var $f2f3e0e3a817edbd$var$UMALQURA_MONTHLENGTH;
var $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE;
function $f2f3e0e3a817edbd$var$umalquraYearStart(year) {
  return $f2f3e0e3a817edbd$var$UMALQURA_START_DAYS + $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE[year - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START];
}
function $f2f3e0e3a817edbd$var$umalquraMonthLength(year, month) {
  let idx = year - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START;
  let mask = 1 << 11 - (month - 1);
  if (($f2f3e0e3a817edbd$var$UMALQURA_MONTHLENGTH[idx] & mask) === 0)
    return 29;
  else
    return 30;
}
function $f2f3e0e3a817edbd$var$umalquraMonthStart(year, month) {
  let day = $f2f3e0e3a817edbd$var$umalquraYearStart(year);
  for (let i = 1; i < month; i++)
    day += $f2f3e0e3a817edbd$var$umalquraMonthLength(year, i);
  return day;
}
function $f2f3e0e3a817edbd$var$umalquraYearLength(year) {
  return $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE[year + 1 - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START] - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE[year - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START];
}
var $f2f3e0e3a817edbd$export$5baab4758c231076 = class extends $f2f3e0e3a817edbd$export$2066795aadd37bfc {
  fromJulianDay(jd) {
    let days = jd - $f2f3e0e3a817edbd$var$CIVIL_EPOC;
    let startDays = $f2f3e0e3a817edbd$var$umalquraYearStart($f2f3e0e3a817edbd$var$UMALQURA_YEAR_START);
    let endDays = $f2f3e0e3a817edbd$var$umalquraYearStart($f2f3e0e3a817edbd$var$UMALQURA_YEAR_END);
    if (days < startDays || days > endDays)
      return super.fromJulianDay(jd);
    else {
      let y = $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START - 1;
      let m = 1;
      let d = 1;
      while (d > 0) {
        y++;
        d = days - $f2f3e0e3a817edbd$var$umalquraYearStart(y) + 1;
        let yearLength = $f2f3e0e3a817edbd$var$umalquraYearLength(y);
        if (d === yearLength) {
          m = 12;
          break;
        } else if (d < yearLength) {
          let monthLength = $f2f3e0e3a817edbd$var$umalquraMonthLength(y, m);
          m = 1;
          while (d > monthLength) {
            d -= monthLength;
            m++;
            monthLength = $f2f3e0e3a817edbd$var$umalquraMonthLength(y, m);
          }
          break;
        }
      }
      return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, y, m, days - $f2f3e0e3a817edbd$var$umalquraMonthStart(y, m) + 1);
    }
  }
  toJulianDay(date) {
    if (date.year < $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START || date.year > $f2f3e0e3a817edbd$var$UMALQURA_YEAR_END)
      return super.toJulianDay(date);
    return $f2f3e0e3a817edbd$var$CIVIL_EPOC + $f2f3e0e3a817edbd$var$umalquraMonthStart(date.year, date.month) + (date.day - 1);
  }
  getDaysInMonth(date) {
    if (date.year < $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START || date.year > $f2f3e0e3a817edbd$var$UMALQURA_YEAR_END)
      return super.getDaysInMonth(date);
    return $f2f3e0e3a817edbd$var$umalquraMonthLength(date.year, date.month);
  }
  getDaysInYear(date) {
    if (date.year < $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START || date.year > $f2f3e0e3a817edbd$var$UMALQURA_YEAR_END)
      return super.getDaysInYear(date);
    return $f2f3e0e3a817edbd$var$umalquraYearLength(date.year);
  }
  constructor() {
    super(), this.identifier = "islamic-umalqura";
    if (!$f2f3e0e3a817edbd$var$UMALQURA_MONTHLENGTH)
      $f2f3e0e3a817edbd$var$UMALQURA_MONTHLENGTH = new Uint16Array(Uint8Array.from(atob($f2f3e0e3a817edbd$var$UMALQURA_DATA), (c) => c.charCodeAt(0)).buffer);
    if (!$f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE) {
      $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE = new Uint32Array($f2f3e0e3a817edbd$var$UMALQURA_YEAR_END - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START + 1);
      let yearStart = 0;
      for (let year = $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START; year <= $f2f3e0e3a817edbd$var$UMALQURA_YEAR_END; year++) {
        $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START_TABLE[year - $f2f3e0e3a817edbd$var$UMALQURA_YEAR_START] = yearStart;
        for (let i = 1; i <= 12; i++)
          yearStart += $f2f3e0e3a817edbd$var$umalquraMonthLength(year, i);
      }
    }
  }
};

// node_modules/@internationalized/date/dist/HebrewCalendar.mjs
var $7c5f6fbf42389787$var$HEBREW_EPOCH = 347997;
var $7c5f6fbf42389787$var$HOUR_PARTS = 1080;
var $7c5f6fbf42389787$var$DAY_PARTS = 24 * $7c5f6fbf42389787$var$HOUR_PARTS;
var $7c5f6fbf42389787$var$MONTH_DAYS = 29;
var $7c5f6fbf42389787$var$MONTH_FRACT = 12 * $7c5f6fbf42389787$var$HOUR_PARTS + 793;
var $7c5f6fbf42389787$var$MONTH_PARTS = $7c5f6fbf42389787$var$MONTH_DAYS * $7c5f6fbf42389787$var$DAY_PARTS + $7c5f6fbf42389787$var$MONTH_FRACT;
function $7c5f6fbf42389787$var$isLeapYear(year) {
  return ($2b4dce13dd5a17fa$export$842a2cf37af977e1)(year * 7 + 1, 19) < 7;
}
function $7c5f6fbf42389787$var$hebrewDelay1(year) {
  let months = Math.floor((235 * year - 234) / 19);
  let parts = 12084 + 13753 * months;
  let day = months * 29 + Math.floor(parts / 25920);
  if (($2b4dce13dd5a17fa$export$842a2cf37af977e1)(3 * (day + 1), 7) < 3)
    day += 1;
  return day;
}
function $7c5f6fbf42389787$var$hebrewDelay2(year) {
  let last = $7c5f6fbf42389787$var$hebrewDelay1(year - 1);
  let present = $7c5f6fbf42389787$var$hebrewDelay1(year);
  let next = $7c5f6fbf42389787$var$hebrewDelay1(year + 1);
  if (next - present === 356)
    return 2;
  if (present - last === 382)
    return 1;
  return 0;
}
function $7c5f6fbf42389787$var$startOfYear(year) {
  return $7c5f6fbf42389787$var$hebrewDelay1(year) + $7c5f6fbf42389787$var$hebrewDelay2(year);
}
function $7c5f6fbf42389787$var$getDaysInYear(year) {
  return $7c5f6fbf42389787$var$startOfYear(year + 1) - $7c5f6fbf42389787$var$startOfYear(year);
}
function $7c5f6fbf42389787$var$getYearType(year) {
  let yearLength = $7c5f6fbf42389787$var$getDaysInYear(year);
  if (yearLength > 380)
    yearLength -= 30;
  switch (yearLength) {
    case 353:
      return 0;
    case 354:
      return 1;
    case 355:
      return 2;
  }
}
function $7c5f6fbf42389787$var$getDaysInMonth(year, month) {
  if (month >= 6 && !$7c5f6fbf42389787$var$isLeapYear(year))
    month++;
  if (month === 4 || month === 7 || month === 9 || month === 11 || month === 13)
    return 29;
  let yearType = $7c5f6fbf42389787$var$getYearType(year);
  if (month === 2)
    return yearType === 2 ? 30 : 29;
  if (month === 3)
    return yearType === 0 ? 29 : 30;
  if (month === 6)
    return $7c5f6fbf42389787$var$isLeapYear(year) ? 30 : 0;
  return 30;
}
var $7c5f6fbf42389787$export$ca405048b8fb5af = class {
  fromJulianDay(jd) {
    let d = jd - $7c5f6fbf42389787$var$HEBREW_EPOCH;
    let m = d * $7c5f6fbf42389787$var$DAY_PARTS / $7c5f6fbf42389787$var$MONTH_PARTS;
    let year = Math.floor((19 * m + 234) / 235) + 1;
    let ys = $7c5f6fbf42389787$var$startOfYear(year);
    let dayOfYear = Math.floor(d - ys);
    while (dayOfYear < 1) {
      year--;
      ys = $7c5f6fbf42389787$var$startOfYear(year);
      dayOfYear = Math.floor(d - ys);
    }
    let month = 1;
    let monthStart = 0;
    while (monthStart < dayOfYear) {
      monthStart += $7c5f6fbf42389787$var$getDaysInMonth(year, month);
      month++;
    }
    month--;
    monthStart -= $7c5f6fbf42389787$var$getDaysInMonth(year, month);
    let day = dayOfYear - monthStart;
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, year, month, day);
  }
  toJulianDay(date) {
    let jd = $7c5f6fbf42389787$var$startOfYear(date.year);
    for (let month = 1; month < date.month; month++)
      jd += $7c5f6fbf42389787$var$getDaysInMonth(date.year, month);
    return jd + date.day + $7c5f6fbf42389787$var$HEBREW_EPOCH;
  }
  getDaysInMonth(date) {
    return $7c5f6fbf42389787$var$getDaysInMonth(date.year, date.month);
  }
  getMonthsInYear(date) {
    return $7c5f6fbf42389787$var$isLeapYear(date.year) ? 13 : 12;
  }
  getDaysInYear(date) {
    return $7c5f6fbf42389787$var$getDaysInYear(date.year);
  }
  getYearsInEra() {
    return 9999;
  }
  getEras() {
    return [
      "AM"
    ];
  }
  balanceYearMonth(date, previousDate) {
    if (previousDate.year !== date.year) {
      if ($7c5f6fbf42389787$var$isLeapYear(previousDate.year) && !$7c5f6fbf42389787$var$isLeapYear(date.year) && previousDate.month > 6)
        date.month--;
      else if (!$7c5f6fbf42389787$var$isLeapYear(previousDate.year) && $7c5f6fbf42389787$var$isLeapYear(date.year) && previousDate.month > 6)
        date.month++;
    }
  }
  constructor() {
    this.identifier = "hebrew";
  }
};

// node_modules/@internationalized/date/dist/EthiopicCalendar.mjs
var $b956b2d7a6cf451f$var$ETHIOPIC_EPOCH = 1723856;
var $b956b2d7a6cf451f$var$COPTIC_EPOCH = 1824665;
var $b956b2d7a6cf451f$var$AMETE_MIHRET_DELTA = 5500;
function $b956b2d7a6cf451f$var$ceToJulianDay(epoch, year, month, day) {
  return epoch + 365 * year + Math.floor(year / 4) + 30 * (month - 1) + day - 1;
}
function $b956b2d7a6cf451f$var$julianDayToCE(epoch, jd) {
  let year = Math.floor(4 * (jd - epoch) / 1461);
  let month = 1 + Math.floor((jd - $b956b2d7a6cf451f$var$ceToJulianDay(epoch, year, 1, 1)) / 30);
  let day = jd + 1 - $b956b2d7a6cf451f$var$ceToJulianDay(epoch, year, month, 1);
  return [
    year,
    month,
    day
  ];
}
function $b956b2d7a6cf451f$var$getLeapDay(year) {
  return Math.floor(year % 4 / 3);
}
function $b956b2d7a6cf451f$var$getDaysInMonth(year, month) {
  if (month % 13 !== 0)
    return 30;
  else
    return $b956b2d7a6cf451f$var$getLeapDay(year) + 5;
}
var $b956b2d7a6cf451f$export$26ba6eab5e20cd7d = class {
  fromJulianDay(jd) {
    let [year, month, day] = $b956b2d7a6cf451f$var$julianDayToCE($b956b2d7a6cf451f$var$ETHIOPIC_EPOCH, jd);
    let era = "AM";
    if (year <= 0) {
      era = "AA";
      year += $b956b2d7a6cf451f$var$AMETE_MIHRET_DELTA;
    }
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, era, year, month, day);
  }
  toJulianDay(date) {
    let year = date.year;
    if (date.era === "AA")
      year -= $b956b2d7a6cf451f$var$AMETE_MIHRET_DELTA;
    return $b956b2d7a6cf451f$var$ceToJulianDay($b956b2d7a6cf451f$var$ETHIOPIC_EPOCH, year, date.month, date.day);
  }
  getDaysInMonth(date) {
    return $b956b2d7a6cf451f$var$getDaysInMonth(date.year, date.month);
  }
  getMonthsInYear() {
    return 13;
  }
  getDaysInYear(date) {
    return 365 + $b956b2d7a6cf451f$var$getLeapDay(date.year);
  }
  getYearsInEra(date) {
    return date.era === "AA" ? 9999 : 9991;
  }
  getEras() {
    return [
      "AA",
      "AM"
    ];
  }
  constructor() {
    this.identifier = "ethiopic";
  }
};
var $b956b2d7a6cf451f$export$d72e0c37005a4914 = class extends $b956b2d7a6cf451f$export$26ba6eab5e20cd7d {
  fromJulianDay(jd) {
    let [year, month, day] = $b956b2d7a6cf451f$var$julianDayToCE($b956b2d7a6cf451f$var$ETHIOPIC_EPOCH, jd);
    year += $b956b2d7a6cf451f$var$AMETE_MIHRET_DELTA;
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, "AA", year, month, day);
  }
  getEras() {
    return [
      "AA"
    ];
  }
  getYearsInEra() {
    return 9999;
  }
  constructor(...args) {
    super(...args), this.identifier = "ethioaa";
  }
};
var $b956b2d7a6cf451f$export$fe6243cbe1a4b7c1 = class extends $b956b2d7a6cf451f$export$26ba6eab5e20cd7d {
  fromJulianDay(jd) {
    let [year, month, day] = $b956b2d7a6cf451f$var$julianDayToCE($b956b2d7a6cf451f$var$COPTIC_EPOCH, jd);
    let era = "CE";
    if (year <= 0) {
      era = "BCE";
      year = 1 - year;
    }
    return new ($35ea8db9cb2ccb90$export$99faa760c7908e4f)(this, era, year, month, day);
  }
  toJulianDay(date) {
    let year = date.year;
    if (date.era === "BCE")
      year = 1 - year;
    return $b956b2d7a6cf451f$var$ceToJulianDay($b956b2d7a6cf451f$var$COPTIC_EPOCH, year, date.month, date.day);
  }
  getDaysInMonth(date) {
    let year = date.year;
    if (date.era === "BCE")
      year = 1 - year;
    return $b956b2d7a6cf451f$var$getDaysInMonth(year, date.month);
  }
  isInverseEra(date) {
    return date.era === "BCE";
  }
  balanceDate(date) {
    if (date.year <= 0) {
      date.era = date.era === "BCE" ? "CE" : "BCE";
      date.year = 1 - date.year;
    }
  }
  getEras() {
    return [
      "BCE",
      "CE"
    ];
  }
  getYearsInEra(date) {
    return date.era === "BCE" ? 9999 : 9715;
  }
  constructor(...args) {
    super(...args), this.identifier = "coptic";
  }
};

// node_modules/@internationalized/date/dist/createCalendar.mjs
function $64244302c3013299$export$dd0bbc9b26defe37(name) {
  switch (name) {
    case "buddhist":
      return new ($8d73d47422ca7302$export$42d20a78301dee44)();
    case "ethiopic":
      return new ($b956b2d7a6cf451f$export$26ba6eab5e20cd7d)();
    case "ethioaa":
      return new ($b956b2d7a6cf451f$export$d72e0c37005a4914)();
    case "coptic":
      return new ($b956b2d7a6cf451f$export$fe6243cbe1a4b7c1)();
    case "hebrew":
      return new ($7c5f6fbf42389787$export$ca405048b8fb5af)();
    case "indian":
      return new ($82c358003bdda0a8$export$39f31c639fa15726)();
    case "islamic-civil":
      return new ($f2f3e0e3a817edbd$export$2066795aadd37bfc)();
    case "islamic-tbla":
      return new ($f2f3e0e3a817edbd$export$37f0887f2f9d22f7)();
    case "islamic-umalqura":
      return new ($f2f3e0e3a817edbd$export$5baab4758c231076)();
    case "japanese":
      return new ($62225008020f0a13$export$b746ab2b60cdffbf)();
    case "persian":
      return new ($f3ed2e4472ae7e25$export$37fccdbfd14c5939)();
    case "roc":
      return new ($5f31bd6f0c8940b2$export$65e01080afcb0799)();
    case "gregory":
    default:
      return new ($3b62074eb05584b2$export$80ee6245ec4f29ec)();
  }
}
var StyledButton = styled(Button)`
  // Should match the DS Link component
  border: 0;
  border-radius: var(--radius-8);
  display: inline-flex;
  font-family: var(--font-sans-serif-regular);
  justify-content: center;
  align-items: center;
  transition: background-color 0.1s ease-in-out;

  &:focus-visible {
    outline: 0px;
  }

  &[data-hovered] {
    cursor: pointer;
  }

  &[data-disabled] {
    opacity: 0.5;
  }

  &.primary {
    background-color: var(--color-surface-button-primary);
    color: var(--color-text-button-primary);

    &[data-hovered] {
      background-color: var(--color-surface-button-primary-hover);
      color: var(--color-text-button-primary-hover);
      span,
      p {
        color: var(--color-text-button-primary-hover);
      }
    }

    &.inverse {
      background-color: var(--color-surface-button-primary-inverse);
      color: var(--color-text-button-primary-inverse);

      &[data-hovered] {
        background-color: var(--color-surface-button-primary-inverse-hover);
        color: var(--color-text-button-primary-inverse-hover);
        span,
        p {
          color: var(--color-text-button-primary-inverse-hover);
        }
      }
    }
  }

  &.secondary {
    background-color: var(--color-surface-button-secondary);
    border: var(--border-width-button-secondary) solid var(--color-border-button-secondary);
    color: var(--color-text-button-secondary);

    &[data-hovered] {
      background-color: var(--color-surface-button-secondary-hover);
      border: var(--border-width-button-secondary-hover) solid var(--color-surface-button-secondary-hover);
      color: var(--color-text-button-secondary-hover);
      span,
      p {
        color: var(--color-text-button-secondary-hover);
      }
    }

    &.inverse {
      background-color: var(--color-surface-button-secondary-inverse);
      border: var(--border-width-button-secondary) solid var(--color-border-button-secondary-inverse);
      color: var(--color-text-button-secondary-inverse);

      &[data-hovered] {
        background-color: var(--color-surface-button-secondary-inverse-hover);
        border: var(--border-width-button-secondary-hover) solid var(--color-surface-button-secondary-inverse-hover);
        color: var(--color-text-button-secondary-inverse-hover);
        span,
        p {
          color: var(--color-text-button-secondary-inverse-hover);
        }
      }
    }
  }

  &.tertiary {
    background-color: var(--transparent);
    border-radius: 0;
    color: var(--color-text-button-tertiary);

    &[data-hovered] {
      color: var(--color-text-button-tertiary-hover);
      span,
      p {
        color: var(--color-text-button-tertiary-hover);
      }
    }

    &.large,
    &.small {
      padding: 0;
    }
  }

  &.large {
    font-size: var(--font-size-button-large);
    line-height: var(--line-height-button-large);
    padding: var(--padding-button-large-y) var(--padding-button-large-x);
  }

  &.small {
    font-size: var(--font-size-button-small);
    line-height: var(--line-height-button-small);
    padding: var(--padding-button-small-y) var(--padding-button-small-x);
  }

  &.full {
    width: 100%;
  }

  &.rounded {
    border-radius: var(--radius-full);
  }

  // If mobile styling needed, make sure to add to buttonStyles.ts, not here
`;
var DSButton = (_a) => {
  var _b = _a, {
    variant = "primary",
    colorScheme = "default",
    size = "large",
    width = "auto",
    children,
    dangerouslySet__rounded: dangerouslySet__rounded
  } = _b, props = __objRest(_b, [
    "variant",
    "colorScheme",
    "size",
    "width",
    "children",
    // eslint-disable-next-line camelcase -- this is match dangerouslySetInnerHTML
    "dangerouslySet__rounded"
  ]);
  const roundedClass = dangerouslySet__rounded ? "rounded" : "";
  return /* @__PURE__ */ jsx(
    StyledButton,
    __spreadProps(__spreadValues(__spreadProps(__spreadValues({}, props), {
      className: `${variant} ${colorScheme} ${size} ${width} ${roundedClass}`
    }), props.testId ? { "data-testid": props.testId } : {}), {
      children
    })
  );
};
var StyledCalendar = styled.div`
  width: 343px;
  border-radius: var(--radius-16);
  background: ${({ $mode }) => $mode === "light" ? "var(--color-surface-neutral)" : "var(--color-surface-navy-bold)"};
  color: ${({ $mode }) => $mode === "light" ? "var(--color-text-primary)" : "var(--color-text-primary-inverse)"};
  border: 1px solid ${({ $mode }) => $mode === "light" ? "var(--color-border)" : "var(--color-border-secondary)"};
`;
var WeekDaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;
var WeekDay = styled.div`
  text-align: center;
`;
var Cell = styled.div`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $isExplicitlyDisabled }) => $isExplicitlyDisabled ? "var(--radius-8)" : "50%"};
  cursor: ${({ $isDisabled }) => $isDisabled ? "not-allowed" : "pointer"};
  transition: all 0.2s;
  pointer-events: ${({ $isDisabled }) => $isDisabled ? "none" : "auto"};
  margin: 4px 0px;

  ${({ $isSelected, $mode }) => $isSelected && `
    background-color: ${$mode === "light" ? "var(--color-surface-navy-subtle)" : "var(--color-surface-yellow-subtle)"};
  `}

  ${({ $isExplicitlyDisabled, $mode }) => $isExplicitlyDisabled && `
    background-color: ${$mode === "light" ? "var(--color-surface-neutral-disabled)" : "var(--blue-500)"};
    border-radius: var(--radius-8);
  `}

  & > * {
    opacity: ${({ $isDisabled }) => $isDisabled ? 0.4 : 1};
  }

  &:hover:not([disabled]) {
    border: 1px solid
      ${({ $mode }) => $mode === "light" ? "var(--color-border-secondary)" : "var(--color-border-inverse)"};
  }
`;
var DSDatePicker = ({
  mode = "light",
  initialDate,
  onChange,
  testId,
  disablePastDates = true,
  disabledDates = []
}) => {
  const { locale } = useLocale();
  const todayDate = $14e0f24ef4ac5c92$export$d0bdf45af03a6ea3($14e0f24ef4ac5c92$export$aa8b41735afcabd2());
  const initialCalendarDate = initialDate ? new $35ea8db9cb2ccb90$export$99faa760c7908e4f(initialDate.getFullYear(), initialDate.getMonth() + 1, initialDate.getDate()) : void 0;
  const validInitialDate = initialCalendarDate && disablePastDates && initialCalendarDate.compare(todayDate) < 0 ? todayDate : initialCalendarDate;
  const disabledCalendarDates = disabledDates.map(
    (date) => new $35ea8db9cb2ccb90$export$99faa760c7908e4f(date.getFullYear(), date.getMonth() + 1, date.getDate())
  );
  const isDateExplicitlyDisabled = (date) => {
    return disabledCalendarDates.some((disabledDate) => date.compare(disabledDate) === 0);
  };
  const isDateUnavailable = (date) => {
    const calDate = date instanceof $35ea8db9cb2ccb90$export$99faa760c7908e4f ? date : new $35ea8db9cb2ccb90$export$99faa760c7908e4f(date.year, date.month, date.day);
    if (disablePastDates && calDate.compare(todayDate) < 0) {
      return true;
    }
    return isDateExplicitlyDisabled(calDate);
  };
  const state = useCalendarState({
    locale,
    createCalendar: $64244302c3013299$export$dd0bbc9b26defe37,
    defaultValue: validInitialDate,
    minValue: disablePastDates ? todayDate : void 0,
    onChange: onChange ? (date) => {
      const jsDate = new Date(date.year, date.month - 1, date.day);
      onChange(jsDate);
    } : void 0,
    isDateUnavailable
  });
  const { prevButtonProps, nextButtonProps, title } = useCalendar(
    {
      minValue: disablePastDates ? todayDate : void 0,
      isDateUnavailable
    },
    state
  );
  const [titleMonth, titleYear] = title.split(" ");
  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    {
      firstDayOfWeek: "sun",
      weekdayStyle: "short"
    },
    state
  );
  const { start, end } = state.visibleRange;
  const firstDayOfMonth = new $35ea8db9cb2ccb90$export$99faa760c7908e4f(start.year, start.month, 1);
  const firstDayOfWeek = firstDayOfMonth.toDate($14e0f24ef4ac5c92$export$aa8b41735afcabd2()).getDay();
  const dates = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    dates.push(null);
  }
  let currentDate = start;
  while (currentDate.compare(end) <= 0) {
    dates.push(currentDate);
    currentDate = currentDate.add({ days: 1 });
  }
  return /* @__PURE__ */ jsxs(StyledCalendar, __spreadProps(__spreadValues({ $mode: mode }, testId ? { "data-testid": testId } : {}), { children: [
    /* @__PURE__ */ jsxs(DSFlex, { direction: "row", children: [
      /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: 16 }),
      /* @__PURE__ */ jsxs(DSFlex, { direction: "column", width: "100%", children: [
        /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 24 }),
        /* @__PURE__ */ jsxs(DSFlex, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [
          /* @__PURE__ */ jsx(
            DSButton,
            {
              variant: "tertiary",
              onPress: (e) => {
                var _a;
                return (_a = prevButtonProps.onPress) == null ? void 0 : _a.call(prevButtonProps, e);
              },
              isDisabled: prevButtonProps.isDisabled,
              "aria-label": prevButtonProps["aria-label"],
              children: /* @__PURE__ */ jsx(DSIcon, { name: "chevron-left", size: 16, color: mode === "light" ? "link" : "inverse" })
            }
          ),
          /* @__PURE__ */ jsxs(DSFlex, { direction: "row", alignItems: "center", children: [
            /* @__PURE__ */ jsx(
              DSText,
              {
                variant: "fluid-paragraph-400",
                weight: "semiBold",
                color: mode === "light" ? "primary" : "primary-inverse",
                children: titleMonth
              }
            ),
            /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: 8 }),
            /* @__PURE__ */ jsx(DSText, { variant: "fluid-paragraph-400", color: mode === "light" ? "primary" : "primary-inverse", children: titleYear })
          ] }),
          /* @__PURE__ */ jsx(
            DSButton,
            {
              variant: "tertiary",
              onPress: (e) => {
                var _a;
                return (_a = nextButtonProps.onPress) == null ? void 0 : _a.call(nextButtonProps, e);
              },
              isDisabled: nextButtonProps.isDisabled,
              "aria-label": nextButtonProps["aria-label"],
              children: /* @__PURE__ */ jsx(DSIcon, { name: "chevron-right", size: 16, color: mode === "light" ? "link" : "inverse" })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 16 }),
        /* @__PURE__ */ jsx(DSDivider, { variant: mode === "light" ? "primary" : "secondary" }),
        /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 16 }),
        /* @__PURE__ */ jsx(WeekDaysGrid, __spreadProps(__spreadValues({}, headerProps), { children: weekDays.map((day, index) => /* @__PURE__ */ jsx(WeekDay, { children: /* @__PURE__ */ jsx(DSText, { variant: "fluid-label-100", color: mode === "light" ? "secondary" : "secondary-inverse", children: day.toUpperCase() }) }, `weekday-${index}`)) })),
        /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 12 }),
        /* @__PURE__ */ jsx(WeekDaysGrid, __spreadProps(__spreadValues({}, gridProps), { children: dates.map((date, i) => {
          if (date === null) {
            return /* @__PURE__ */ jsx(Cell, { $mode: mode, $isDisabled: true }, `empty-${i}`);
          }
          const isSelected = state.isSelected(date);
          const isDisabled = isDateUnavailable(date);
          const isExplicitlyDisabled = isDateExplicitlyDisabled(date);
          const isToday = date.compare(todayDate) === 0;
          return /* @__PURE__ */ jsx(
            Cell,
            {
              $mode: mode,
              $isSelected: isSelected,
              $isDisabled: isDisabled,
              $isExplicitlyDisabled: isExplicitlyDisabled,
              onClick: isDisabled ? void 0 : () => {
                state.selectDate(date);
              },
              children: /* @__PURE__ */ jsx(
                DSText,
                {
                  variant: "fluid-paragraph-200",
                  weight: isToday ? "semiBold" : "light",
                  color: isSelected ? mode === "light" ? "link" : "link-navigation-bold" : isToday ? mode === "light" ? "link" : "link-inverse" : mode === "light" ? "primary" : "primary-inverse",
                  children: date.day
                }
              )
            },
            i
          );
        }) }))
      ] }),
      /* @__PURE__ */ jsx(DSSpacer, { direction: "horizontal", size: 16 })
    ] }),
    /* @__PURE__ */ jsx(DSSpacer, { direction: "vertical", size: 8 })
  ] }));
};
var StyledIconGrid = styled.div`
  ${({ $surfaceColor }) => $surfaceColor ? `background-color: var(--color-surface-${$surfaceColor});` : ""}
  display: flex;
  flex-wrap: wrap;
  gap: var(--grid-16);
`;
var StyledIconTextContentContainer = styled.div`
  max-width: 142px;
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  align-items: center;
`;
var DSIconGrid = ({
  surfaceColor,
  iconColor = "primary",
  textColor = "primary",
  cells = [],
  testId
}) => {
  return /* @__PURE__ */ jsx(StyledIconGrid, __spreadProps(__spreadValues({ $surfaceColor: surfaceColor }, testId ? { "data-testid": testId } : {}), { children: cells.map((cell, i) => /* @__PURE__ */ jsxs(React2.Fragment, { children: [
    i > 0 && /* @__PURE__ */ jsx(DSDivider, { direction: "vertical" }),
    /* @__PURE__ */ jsxs(StyledIconTextContentContainer, { children: [
      /* @__PURE__ */ jsx(DSSpacer, { size: 16, direction: "vertical" }),
      /* @__PURE__ */ jsx(DSIcon, { name: cell.icon, size: 32, color: iconColor }),
      /* @__PURE__ */ jsx(DSSpacer, { size: 12, direction: "vertical" }),
      /* @__PURE__ */ jsx(DSText, { variant: "fluid-paragraph-200", align: "center", color: textColor, children: cell.label }),
      /* @__PURE__ */ jsx(DSSpacer, { size: 16, direction: "vertical" })
    ] })
  ] }, i)) }));
};
var blur = keyframes`
  from {
    background: rgba(45 0 0 / 0);
    backdrop-filter: blur(0);
  }

  to {
    background: rgba(45 0 0 / .3);
    backdrop-filter: blur(10px);
  }
`;
var fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
var slideIn = keyframes`
  from {
    transform: translateY(-20%);
  }
  to {
    transform: translateY(0);
  }
`;
var StyledModal = styled(Modal)`
  position: fixed;
  z-index: 900; // goes on top of header, change header context
`;
var StyledBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-out;
  &[data-entering] {
    animation: ${blur} 300ms;
  }

  &[data-exiting] {
    animation: ${blur} 300ms reverse ease-in;
  }
`;
var StyledModalWrapper = styled.div`
  border-radius: var(--radius-8);
  padding: var(--grid-24) var(--grid-16);
  max-width: 500px;
  width: 100%;
  animation: ${slideIn} 0.3s ease-out;
  position: relative;
  max-height: 100vh;
  overflow-y: auto;
  ${({ $surfaceColor }) => `background-color: var(--color-surface-${$surfaceColor});`}
`;
var StyledDialogTriggerContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: var(--grid-24);
  right: var(--grid-16);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: filter 0.3s ease-out;
  ${({ $surfaceColor }) => `background-color: var(--color-surface-${$surfaceColor});`}

  &:hover {
    filter: invert(100%);
  }
`;
function DSModal({
  surfaceColor = "neutral",
  children,
  isOpen,
  onBackdropClick,
  onCloseButtonClick,
  testId
}) {
  return /* @__PURE__ */ jsx(StyledModal, __spreadProps(__spreadValues({ isOpen }, testId ? { "data-testid": testId } : {}), { children: /* @__PURE__ */ jsx(Dialog, { children: /* @__PURE__ */ jsx(StyledBackdrop, { onClick: onBackdropClick, children: /* @__PURE__ */ jsxs(
    StyledModalWrapper,
    {
      $surfaceColor: surfaceColor,
      onClick: (e) => {
        e.stopPropagation();
      },
      children: [
        children,
        onCloseButtonClick ? /* @__PURE__ */ jsx(StyledDialogTriggerContainer, { $surfaceColor: surfaceColor, onClick: onCloseButtonClick, children: /* @__PURE__ */ jsx(DialogTrigger, { children: /* @__PURE__ */ jsx(DSIcon, { name: "close", size: 16 }) }) }) : null
      ]
    }
  ) }) }) }));
}
var StyledRadioGroup = styled(RadioGroup)`
  ${({ $surfaceColor }) => $surfaceColor && `
    background: var(--color-surface-${$surfaceColor});
    padding: var(--grid-16);
    border-radius: var(--radius-8);
  `}
`;
var StyledRadio = styled(Radio)`
  display: flex;
  align-items: center;

  &:before {
    content: '';
    display: block;
    width: 24px;
    height: 24px;
    box-sizing: border-box;
    outline: 1px solid var(--color-border);
    outline-offset: 1px;
    background: var(--color-surface-neutral);
    border-radius: 50%;
    transition: outline 200ms;
  }

  &[data-pressed]:before {
    outline-color: var(--color-border-focused);
  }

  &[data-selected] {
    &:before {
      outline: 1px solid var(--color-border);
      border: 5px solid var(--color-surface-neutral);
      background: var(--color-border-secondary);
    }

    &[data-pressed]:before {
      outline-color: var(--color-border-focused);
    }
  }

  &[data-focus-visible]:before {
    outline: 2px solid var(--color-border-focused);
    outline-offset: 2px;
  }

  span {
    margin-left: var(--grid-8);
  }
`;
function DSRadioButtonGroup({
  label,
  surfaceColor,
  textColor = "primary",
  options,
  onChange,
  initialValue,
  name,
  testId
}) {
  return /* @__PURE__ */ jsxs(
    StyledRadioGroup,
    __spreadProps(__spreadValues({
      $surfaceColor: surfaceColor,
      onChange,
      value: initialValue,
      name
    }, testId ? { "data-testid": testId } : {}), {
      children: [
        label ? /* @__PURE__ */ jsxs(Label, { children: [
          /* @__PURE__ */ jsx(DSText, { as: "span", variant: "fluid-paragraph-200", color: textColor, children: label }),
          /* @__PURE__ */ jsx(DSSpacer, { size: 12, direction: "vertical" })
        ] }) : null,
        /* @__PURE__ */ jsx(DSFlex, { direction: "row", gap: 24, children: options.map((option) => /* @__PURE__ */ jsx(StyledRadio, { value: option.value, children: /* @__PURE__ */ jsx(DSText, { as: "span", variant: "fluid-paragraph-200", color: textColor, children: option.label }) }, option.value)) })
      ]
    })
  );
}
var StyledSliderWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;
var StyledRangeInput = styled.input.attrs({ type: "range" })`
  -webkit-appearance: none;
  appearance: none;
  background: var(--color-surface-white);
  border-radius: 100px;
  height: 12px;
  margin: 0;
  outline: none;
  position: relative;
  width: 100%;
  z-index: 1;

  &::-webkit-slider-runnable-track {
    background: var(--color-surface-white);
    border-radius: 100px;
    cursor: pointer;
    height: 12px;
    width: 100%;
  }

  &::-moz-range-track {
    background: var(--color-surface-white);
    border-radius: 100px;
    cursor: pointer;
    height: 12px;
    width: 100%;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    background: var(--color-surface-aubergine-bold);
    border-radius: 50%;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    visibility: ${({ $touched }) => $touched ? "visible" : "hidden"};
    height: 24px;
    position: relative;
    top: -6px;
    transition: transform 0.3s ease;
    width: 24px;
    z-index: 5;
  }

  &::-moz-range-thumb {
    background: var(--color-surface-aubergine-bold);
    border-radius: 50%;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    height: 24px;
    position: relative;
    transition: transform 0.3s ease;
    width: 24px;
    z-index: 5;
  }

  &:hover::-webkit-slider-thumb {
    transform: scale(1.1);
  }

  &:hover::-moz-range-thumb {
    transform: scale(1.1);
  }
`;
var StyledDotsBackground = styled.div`
  background: ${({ value, min, max, $touched }) => {
  if (!$touched)
    return "var(--color-surface-white);";
  const percentage = (value - min) / (max - min) * 100;
  return `linear-gradient(to right, var(--color-surface-aubergine-bold) ${percentage.toString()}%%, transparent ${percentage.toString()}%)`;
}};
  border-radius: 100px;
  display: flex;
  left: 0;
  height: 12px;
  justify-content: space-between;
  padding: 0 6px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  z-index: 2;
`;
var StyledDot = styled.div`
  background-color: var(--color-text-secondary-inverse);
  border-radius: 50%;
  height: 4px;
  margin: 4px 4px 0 4px;
  width: 4px;
  z-index: 2;

  &:hover {
    transform: scale(1.1);
  }
`;
var StyledUntouchedRangeDot = styled.div`
  border: 10px solid var(--color-surface-aubergine-bold);
  border-radius: 50%;
  background: var(--color-text-secondary-inverse);
  height: 24px;
  left: calc(50% - 12px);
  position: absolute;
  width: 24px;
  z-index: 5;

  &:hover {
    transform: scale(1.1);
  }
`;
function DSRangeSlider({ min = 0, max = 5, step = 1, name, onChange, testId }) {
  const [value, setValue] = useState(min);
  const [touched, setTouched] = useState(false);
  const handleChange = (e) => {
    onChange && onChange(e);
    setValue(Number(e.target.value));
    setTouched(true);
  };
  return /* @__PURE__ */ jsxs(StyledSliderWrapper, __spreadProps(__spreadValues({}, testId ? { "data-testid": testId } : {}), { children: [
    /* @__PURE__ */ jsx(StyledDotsBackground, { $touched: touched, max, min, value, children: Array.from({ length: max - min + 1 }).map((_, index) => /* @__PURE__ */ jsx(StyledDot, {}, index)) }),
    !touched && /* @__PURE__ */ jsx(StyledUntouchedRangeDot, {}),
    /* @__PURE__ */ jsx(
      StyledRangeInput,
      {
        $touched: touched,
        max,
        min,
        name,
        onChange: handleChange,
        step,
        value
      }
    )
  ] }));
}
var StyledContainer = styled.div`
  display: flex;
  gap: var(--grid-16);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  justify-content: ${({ $isOverflowing }) => $isOverflowing ? "flex-start" : "center"};
  // Hide scrollbar
  scrollbar-width: none; /* Firefox 64 */
  -ms-overflow-style: none; /* Internet Explorer 11 */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;
var StyledSlide = styled.div`
  flex: 0 0 auto;
  scroll-snap-align: start;
  width: ${({ $width }) => `${$width.toString()}px`};
`;
var StyledActiveIndex = styled.span`
  display: inline-block;
  width: 72px;
  height: 8px;
  background: ${({ $paginationColor }) => `var(--color-surface-${$paginationColor})`};
  border-radius: var(--radius-full);
`;
var StyledDot2 = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  background: ${({ $paginationColor }) => `var(--color-surface-${$paginationColor})`};
  opacity: 0.16;
  border-radius: 50%;
  cursor: pointer;
`;
var StyledSliderNavigator = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--grid-12);
`;
var DSSlider = ({
  slideWidth = 200,
  paginationColor = "yellow-bold",
  testId,
  children
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef(null);
  const validChildrenCount = Children.count(Children.toArray(children).filter(isValidElement));
  const handleScroll = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      const scrollLeft = container.scrollLeft;
      const totalWidth = container.scrollWidth;
      const containerWidth = container.offsetWidth;
      let currentIndex = Math.round(scrollLeft / slideWidth);
      if (scrollLeft + containerWidth >= totalWidth) {
        currentIndex = validChildrenCount - 1;
      }
      setActiveIndex(currentIndex);
    }
  };
  const handleDotClick = (index) => {
    if (containerRef.current) {
      const slideWidthWithGap = slideWidth + 16;
      const scrollPosition = index * slideWidthWithGap;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });
    }
  };
  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const totalSlidesWidth = validChildrenCount * slideWidth + (validChildrenCount - 1) * 16;
      setIsOverflowing(totalSlidesWidth > containerWidth);
    }
  }, [children, slideWidth, validChildrenCount]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      StyledContainer,
      __spreadProps(__spreadValues({
        ref: containerRef,
        onScroll: handleScroll,
        $isOverflowing: isOverflowing
      }, testId ? { "data-testid": testId } : {}), {
        children: Children.map(
          children,
          (child) => {
            var _a;
            return isValidElement(child) ? /* @__PURE__ */ jsx(StyledSlide, { $width: slideWidth, children: child }, (_a = child.key) != null ? _a : void 0) : null;
          }
        )
      })
    ),
    isOverflowing && validChildrenCount > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(DSSpacer, { size: 24, direction: "vertical" }),
      /* @__PURE__ */ jsx(StyledSliderNavigator, { children: Array.from(
        { length: validChildrenCount },
        (_, index) => activeIndex === index ? /* @__PURE__ */ jsx(StyledActiveIndex, { $paginationColor: paginationColor }, index) : /* @__PURE__ */ jsx(
          StyledDot2,
          {
            $paginationColor: paginationColor,
            onClick: () => {
              handleDotClick(index);
            }
          },
          index
        )
      ) })
    ] }) : null
  ] });
};
var StyledSurface = styled.div`
  ${({ $surfaceColor }) => `background-color: var(--color-surface-${$surfaceColor});`}
  ${({ $radius }) => $radius && ($radius === "full" ? "border-radius: var(--radius-full);" : `border-radius: var(--radius-${$radius.toString()});`)}
  ${({ $padding }) => $padding && `padding: var(--grid-${$padding.toString()});`}
`;
var DSSurface = ({
  surfaceColor = "neutral",
  radius = 0,
  padding = 0,
  children,
  testId
}) => {
  return /* @__PURE__ */ jsx(
    StyledSurface,
    __spreadProps(__spreadValues({
      $surfaceColor: surfaceColor,
      $radius: radius,
      $padding: padding
    }, testId ? { "data-testid": testId } : {}), {
      children
    })
  );
};
var TabContainer = styled.div`
  width: 100%;
  flex: 1;
  padding: var(--grid-12) 0;
  cursor: pointer;
  position: relative;

  @media (min-width: ${BREAKPOINTS.l}) {
    max-width: 144px;
    flex: none;
  }
`;
var Indicator = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: var(--color-text-primary);
  opacity: ${(props) => props.$isActive ? 1 : 0};
  transition: 300ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(calc(-100% * (${(props) => props.$index} - ${(props) => props.$selectedIndex})));
`;
var Tab = (props) => {
  const { onPress, title, isActive, index, selectedIndex } = props;
  return /* @__PURE__ */ jsxs(TabContainer, { onClick: onPress, children: [
    /* @__PURE__ */ jsx(DSText, { variant: "fluid-paragraph-200", weight: "semiBold", color: "primary", align: "center", children: title }),
    /* @__PURE__ */ jsx(Indicator, { $index: index, $selectedIndex: selectedIndex, $isActive: isActive })
  ] });
};
var TabsHeaderContainer = styled.div`
  @media (max-width: ${BREAKPOINTS.m}) {
    display: none;
  }
`;
var TabList = styled.div`
  display: flex;
  gap: var(--grid-32);
`;
var TabsHeader = ({ tabTitles, selectedIndex = 0, setSelectedIndex }) => {
  const tabsRef = useRef(null);
  return /* @__PURE__ */ jsx(TabsHeaderContainer, { children: /* @__PURE__ */ jsx(TabList, { ref: tabsRef, children: tabTitles.map((title, index) => /* @__PURE__ */ jsx(
    Tab,
    {
      title,
      onPress: () => {
        setSelectedIndex(index);
      },
      isActive: selectedIndex === index,
      index,
      selectedIndex
    },
    title
  )) }) });
};
var TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--grid-48);
`;
var StyledTabItem = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: ${BREAKPOINTS.m}) {
    opacity: 0;
    transition: all 800ms cubic-bezier(0.4, 0, 0.2, 1);

    &.active {
      opacity: 1;
    }

    &:not(.active) {
      display: none;
    }
  }
`;
var DSTabs = ({ tabs, onTabChange, initialIndex, testId }) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex != null ? initialIndex : 0);
  const [prevIndex, setPrevIndex] = useState(selectedIndex);
  const titles = tabs.map((tab) => tab.title);
  const onTabChanged = useCallback(
    (index) => {
      if (index === selectedIndex)
        return;
      setPrevIndex(selectedIndex);
      setSelectedIndex(index);
      onTabChange == null ? void 0 : onTabChange(index);
    },
    [selectedIndex, setSelectedIndex, onTabChange]
  );
  const slideDirection = selectedIndex > prevIndex ? "left" : "right";
  return /* @__PURE__ */ jsxs(TabsContainer, __spreadProps(__spreadValues({}, testId ? { "data-testid": testId } : {}), { children: [
    /* @__PURE__ */ jsx(TabsHeader, { tabTitles: titles, selectedIndex, setSelectedIndex: onTabChanged }),
    tabs.map((tab, index) => /* @__PURE__ */ jsx(StyledTabItem, { $direction: slideDirection, className: index === selectedIndex ? "active" : "", children: tab.content }, tab.title))
  ] }));
};
var DSTextAndIcon = (_a) => {
  var _b = _a, {
    children,
    icon,
    alignment = "center",
    color: color = "primary",
    align = "inherit",
    variant,
    weight = "light",
    textDecoration,
    breakAt,
    testId
  } = _b, restProps = __objRest(_b, [
    "children",
    "icon",
    "alignment",
    // Inherit all DSText defaults
    "color",
    "align",
    "variant",
    "weight",
    "textDecoration",
    "breakAt",
    "testId"
  ]);
  var _a2;
  const iconPosition = (_a2 = icon.position) != null ? _a2 : "left";
  return /* @__PURE__ */ jsx(
    DSFlex,
    __spreadProps(__spreadValues({
      direction: "row",
      justifyContent: "flex-start",
      alignItems: alignment
    }, testId ? { "data-testid": testId } : {}), {
      children: /* @__PURE__ */ jsxs(DSFlex, { direction: iconPosition === "right" ? "row" : "row-reverse", alignItems: alignment, children: [
        /* @__PURE__ */ jsx(
          DSText,
          __spreadProps(__spreadValues({
            as: "span",
            variant,
            color,
            align,
            weight,
            textDecoration,
            breakAt,
            testId: `${testId != null ? testId : ""}-text`
          }, restProps), {
            children
          })
        ),
        /* @__PURE__ */ jsx(DSSpacer, { size: 8, direction: "horizontal" }),
        /* @__PURE__ */ jsx(DSIcon, { name: icon.name, size: icon.size, color: icon.color, testId: `${testId != null ? testId : ""}-icon` })
      ] })
    })
  );
};
var buttonStyles = css`
  // Large button
  --font-size-button-large: var(--font-size-16);
  --line-height-button-large: var(--line-height-28);
  --padding-button-large-y: var(--grid-16);
  --padding-button-large-x: var(--grid-32);
  --border-width-button-secondary: 1px;
  --border-width-button-secondary-hover: 1px;

  // Small button
  --font-size-button-small: var(--font-size-14);
  --line-height-button-small: var(--line-height-22);
  --padding-button-small-y: var(--grid-8);
  --padding-button-small-x: var(--grid-16);

  // Primary button
  --color-surface-button-primary: var(--color-surface-yellow-bold);
  --color-surface-button-primary-hover: var(--yellow-500);
  --color-text-button-primary: var(--color-text-primary);
  --color-text-button-primary-hover: var(--color-text-primary);

  // Primary button inverse
  --color-surface-button-primary-inverse: var(--color-surface-navy-bold);
  --color-surface-button-primary-inverse-hover: var(--blue-500);
  --color-text-button-primary-inverse: var(--color-text-primary-inverse);
  --color-text-button-primary-inverse-hover: var(--color-text-primary-inverse);

  // Secondary button
  --color-border-button-secondary: var(--color-text-primary);
  --color-surface-button-secondary: var(--transparent);
  --color-surface-button-secondary-hover: var(--color-surface-navy-bold);
  --color-text-button-secondary: var(--color-text-primary);
  --color-text-button-secondary-hover: var(--color-text-primary-inverse);

  // Secondary button inverse
  --color-border-button-secondary-inverse: var(--color-border-inverse);
  --color-surface-button-secondary-inverse: var(--transparent);
  --color-surface-button-secondary-inverse-hover: var(--color-surface-yellow-bold);
  --color-text-button-secondary-inverse: var(--color-text-primary-inverse);
  --color-text-button-secondary-inverse-hover: var(--color-text-primary);

  // Tertiary button
  --color-text-button-tertiary: var(--color-text-link);
  --color-text-button-tertiary-hover: var(--color-text-link-hover);
`;
var colorVariables = css`
  /* Color definitions */
  --aubergine-bold: hsla(336, 30%, 25%, 1);

  --blue-300: hsla(229, 12%, 34%, 1);
  --blue-400: hsla(228, 22%, 23%, 1);
  --blue-400-70: hsla(228, 22%, 23%, 0.7);
  --blue-400-15: hsla(228, 22%, 23%, 0.15);
  --blue-500: hsla(228, 22%, 18%, 1);

  --cream-100: hsla(36, 33%, 97%, 1);

  --dark-neutral-700: hsla(195, 2%, 44%, 1);

  --indigo-400: hsla(241, 55%, 57%, 1);
  --indigo-500: hsla(241, 61%, 47%, 1);

  --light-neutral-100: hsla(0, 0%, 100%, 1);
  --light-neutral-200: hsla(0, 0%, 97%, 1);
  --light-neutral-400: hsla(0, 0%, 89%, 1);

  --red-100: hsla(11, 65%, 90%, 1);
  --red-400: hsla(10, 68%, 44%, 1);

  --score-excellent: hsla(67, 98%, 34%, 1);

  --teal-100: hsla(170, 27%, 96%, 1);

  --transparent: transparent;

  --yellow-400: hsla(49, 100%, 50%, 1);
  --yellow-500: hsla(44, 77%, 58%, 1);

  --green-400: hsla(139, 47%, 34%, 1);

  --white: hsla(0, 0%, 100%, 1);

  --lime-500: hsla(81, 32%, 74%, 1);

  /* Text - main */
  --color-text-primary: var(--blue-400);
  --color-text-primary-inverse: var(--light-neutral-100);
  --color-text-secondary: var(--blue-300);
  --color-text-secondary-inverse: var(--light-neutral-400);
  --color-text-placeholder: var(--dark-neutral-700);

  /* Text - state */
  --color-text-error: var(--red-400);

  /* Text - link */
  --color-text-link: var(--indigo-400);
  --color-text-link-hover: var(--indigo-500);
  --color-text-link-inverse: var(--light-neutral-100);
  --color-text-link-inverse-hover: var(--light-neutral-200);
  --color-text-link-navigation-bold: var(--blue-400);
  --color-text-link-navigation-bold-hover: var(--indigo-500);

  /* Surface - main */
  --color-surface-neutral: var(--light-neutral-100);
  --color-surface-neutral-disabled: var(--light-neutral-200);
  --color-surface-yellow-bold: var(--yellow-400);
  --color-surface-navy-bold: var(--blue-400);
  --color-surface-aubergine-bold: hsla(336, 30%, 25%, 1);
  --color-surface-teal-bold: hsla(180, 19%, 22%, 1);
  --color-surface-white: var(--white);
  --color-surface-error: var(--red-100);

  /* Surface - decorative */
  --color-surface-cream-subtle: hsla(53, 36%, 96%, 1);
  --color-surface-cream-bold: hsla(53, 43%, 89%, 1);
  --color-surface-yellow-subtle: hsla(49, 100%, 86%, 1);
  --color-surface-navy-subtle: hsla(229, 52%, 96%, 1);
  --color-surface-aubergine-subtle: hsla(353, 31%, 94%, 1);
  --color-surface-teal-subtle: hsla(165, 31%, 95%, 1);
  --color-surface-teal-medium: hsla(162, 30%, 83%, 1);

  /* Surface - overlay */
  --color-surface-blanket: var(--blue-400-70);

  /* Surface - label */
  --color-surface-label-green: var(--score-excellent);

  /* Border - default */
  --color-border: var(--light-neutral-400);
  --color-border-secondary: var(--blue-400);
  --color-border-inverse: var(--light-neutral-100);
  --color-border-teal: hsla(180, 19%, 22%, 0.1);

  /* Border - state */
  --color-border-disabled: var(--light-neutral-200);
  --color-border-focused: var(--indigo-400);
  --color-border-error: var(--red-400);
  --color-border-blue-subtle: var(--blue-400-15);

  /* Icon tokens */
  --color-icon-primary: var(--blue-400);
  --color-icon-border: var(--blue-400);
  --color-icon-secondary: var(--dark-neutral-700);
  --color-icon-inverse: var(--light-neutral-100);
  --color-icon-success: var(--green-400);
  --color-icon-error: var(--red-400);
  --color-icon-brand: var(--yellow-400);
  --color-icon-lime: var(--lime-500);
  --color-icon-link: var(--indigo-400);
  --color-icon-hover: var(--indigo-500);

  /* Divider */
  --color-divider-primary: var(--light-neutral-400);
  --color-divider-secondary: hsla(0, 0%, 89%, 0.25);
  --color-divider-tertiary: var(--color-border-blue-subtle);
  --color-divider-brand: var(--color-surface-yellow-bold);

  // TODO: Remove after migration
  // @deprecated

  /* Text - main */
  --colour-text-primary: var(--blue-400);
  --colour-text-primary-inverse: var(--light-neutral-100);
  --colour-text-secondary: var(--blue-300);
  --colour-text-secondary-inverse: var(--light-neutral-400);
  --colour-text-placeholder: var(--dark-neutral-700);

  /* Text - state */
  --colour-text-error: var(--red-400);

  /* Text - link */
  --colour-text-link: var(--indigo-400);
  --colour-text-link-hover: var(--indigo-500);
  --colour-text-link-inverse: var(--light-neutral-100);
  --colour-text-link-inverse-hover: var(--light-neutral-200);
  --colour-text-link-navigation-bold: var(--blue-400);
  --colour-text-link-navigation-bold-hover: var(--indigo-500);

  /* Surface - main */
  --colour-surface-neutral: var(--light-neutral-100);
  --colour-surface-neutral-disabled: var(--light-neutral-200);
  --colour-surface-yellow-bold: var(--yellow-400);
  --colour-surface-navy-bold: var(--blue-400);
  --colour-surface-aubergine-bold: hsla(336, 30%, 25%, 1);
  --colour-surface-teal-bold: hsla(180, 19%, 22%, 1);
  --colour-surface-white: var(--white);
  --colour-surface-error: var(--red-100);

  /* Surface - decorative */
  --colour-surface-cream-subtle: hsla(53, 36%, 96%, 1);
  --colour-surface-cream-bold: hsla(53, 43%, 89%, 1);
  --colour-surface-yellow-subtle: hsla(49, 100%, 86%, 1);
  --colour-surface-navy-subtle: hsla(229, 52%, 96%, 1);
  --colour-surface-aubergine-subtle: hsla(353, 31%, 94%, 1);
  --colour-surface-teal-subtle: hsla(165, 31%, 95%, 1);
  --colour-surface-teal-medium: hsla(162, 30%, 83%, 1);

  /* Surface - overlay */
  --colour-surface-blanket: var(--blue-400-70);

  /* Surface - label */
  --colour-surface-label-green: var(--score-excellent);

  /* Border - default */
  --colour-border: var(--light-neutral-400);
  --colour-border-secondary: var(--blue-400);
  --colour-border-inverse: var(--light-neutral-100);
  --colour-border-teal: hsla(180, 19%, 22%, 0.1);

  /* Border - state */
  --colour-border-disabled: var(--light-neutral-200);
  --colour-border-focused: var(--indigo-400);
  --colour-border-error: var(--red-400);
  --colour-border-blue-subtle: var(--blue-400-15);

  /* Icon tokens */
  --colour-icon-primary: var(--blue-400);
  --colour-icon-border: var(--blue-400);
  --colour-icon-secondary: var(--dark-neutral-700);
  --colour-icon-inverse: var(--light-neutral-100);
  --colour-icon-success: var(--green-400);
  --colour-icon-error: var(--red-400);
  --colour-icon-brand: var(--yellow-400);
  --colour-icon-lime: var(--lime-500);
  --colour-icon-link: var(--indigo-400);
  --colour-icon-hover: var(--indigo-500);

  /* Divider */
  --colour-divider-primary: var(--light-neutral-400);
  --colour-divider-secondary: hsla(0, 0%, 89%, 0.25);
  --colour-divider-tertiary: var(--color-border-blue-subtle);
  --colour-divider-brand: var(--color-surface-yellow-bold);
`;
var fontFaceDeclarations = css`
  @font-face {
    font-family: 'LotaGrotesqueAlt1';
    src:
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Light/font.woff2') format('woff2'),
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Light/font.woff') format('woff');
    font-weight: 300;
  }

  @font-face {
    font-family: 'LotaGrotesqueAlt1-Light';
    src:
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Light/font.woff2') format('woff2'),
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Light/font.woff') format('woff');
    font-weight: 300;
  }

  @font-face {
    font-family: 'LotaGrotesqueAlt1';
    src:
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Regular/font.woff2') format('woff2'),
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Regular/font.woff') format('woff');
    font-weight: 400;
  }

  @font-face {
    font-family: 'LotaGrotesqueAlt1-regular';
    src:
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Regular/font.woff2') format('woff2'),
      url('https://zoe.com/fonts/LotaGrotesqueAlt1Regular/font.woff') format('woff');
    font-weight: 400;
  }

  @font-face {
    font-family: 'LotaGrotesqueAlt1-SemiBold';
    src:
      url('https://zoe.com/fonts/LotaGrotesqueAlt1SemiBold/font.woff2') format('woff2'),
      url('https://zoe.com/fonts/LotaGrotesqueAlt1SemiBold/font.woff') format('woff');
    font-weight: 400;
  }

  @font-face {
    font-family: 'Nantes';
    src:
      url('https://zoe.com/fonts/Nantes_Web_Book/NantesWeb-Book.woff2') format('woff2'),
      url('https://zoe.com/fonts/Nantes_Web_Book/NantesWeb-Book.woff') format('woff');
    font-weight: 300;
    font-style: normal;
    font-display: block;
  }

  @font-face {
    font-family: 'Nantes-Italic';
    src:
      url('https://zoe.com/fonts/Nantes_Web_BookItalic/NantesWeb-BookItalic.woff2') format('woff2'),
      url('https://zoe.com/fonts/Nantes_Web_BookItalic/NantesWeb-BookItalic.woff') format('woff');
    font-weight: 300;
  }
`;
var fontVariables = css`
  --font-fallback-sans-serif: 'sans-serif';
  --font-fallback-serif: 'serif';
  --font-serif: 'Nantes';
  --font-serif-weight: 300;
  --font-serif-italic: 'Nantes-Italic';
  --font-serif-italic-weight: 300;
  --font-sans-serif-light: 'LotaGrotesqueAlt1-Light';
  --font-sans-serif-light-weight: 300;
  --font-sans-serif-regular: 'LotaGrotesqueAlt1-Regular';
  --font-sans-serif-regular-weight: 400;
  --font-sans-serif-semibold: 'LotaGrotesqueAlt1-SemiBold';
  --font-sans-serif-semibold-weight: 400;

  --font-size-14: 0.875rem; // 14px
  --font-size-16: 1rem; // 16px
  --font-size-18: 1.125rem; // 18px
  --font-size-20: 1.25rem; // 20px
  --font-size-22: 1.375rem; // 22px
  --font-size-24: 1.5rem; // 24px
  --font-size-30: 1.875rem; // 30px
  --font-size-32: 2rem; // 32px
  --font-size-34: 2.125rem; // 34px
  --font-size-36: 2.25rem; // 36px
  --font-size-40: 2.5rem; // 40px
  --font-size-44: 2.75rem; // 44px
  --font-size-46: 2.875rem; // 46px
  --font-size-56: 3.5rem; // 56px
  --font-size-72: 4.5rem; // 72px
  --font-size-100: 6.25rem; // 100px

  --line-height-22: 1.375rem; // 22px
  --line-height-24: 1.5rem; // 24px
  --line-height-28: 1.75rem; // 28px
  --line-height-30: 1.875rem; // 30px
  --line-height-32: 2; // 32px

  --letter-spacing-ratio-heading: -0.02; // -2% of font size
  --letter-spacing-ratio-eyebrow: 0.05; // 5% of font size
  --line-height-ratio-heading: 1.05; // 105% of font size
  --line-height-ratio-paragraph: 1.2; // 120% of font size

  --font-size-fluid-label-100: var(--font-size-14);
  --line-height-fluid-label-100: calc(var(--font-size-14) * var(--line-height-ratio-paragraph));
  --font-size-fluid-paragraph-200: var(--font-size-16);
  --line-height-fluid-paragraph-200: calc(var(--font-size-fluid-paragraph-200) * var(--line-height-ratio-paragraph));
  --font-size-fluid-paragraph-300: var(--font-size-18);
  --line-height-fluid-paragraph-300: calc(var(--font-size-fluid-paragraph-300) * var(--line-height-ratio-paragraph));
  --font-size-fluid-paragraph-400: var(--font-size-20);
  --line-height-fluid-paragraph-400: calc(var(--font-size-fluid-paragraph-400) * var(--line-height-ratio-paragraph));
  --font-size-fluid-heading-500: var(--font-size-24);
  --line-height-fluid-heading-500: calc(var(--font-size-fluid-heading-500) * var(--line-height-ratio-heading));
  --line-height-fluid-heading-500-alt: var(
    --line-height-30
  ); // sans-serif font therefore doesn't follow line height ratio for headings
  --font-size-fluid-heading-600: var(--font-size-30);
  --line-height-fluid-heading-600: calc(var(--font-size-fluid-heading-600) * var(--line-height-ratio-heading));
  --font-size-fluid-heading-700: var(--font-size-36);
  --line-height-fluid-heading-700: calc(var(--font-size-fluid-heading-700) * var(--line-height-ratio-heading));
  --font-size-fluid-heading-800: var(--font-size-40);
  --line-height-fluid-heading-800: calc(var(--font-size-fluid-heading-800) * var(--line-height-ratio-heading));
  --font-size-fluid-heading-900: var(--font-size-46);
  --line-height-fluid-heading-900: calc(var(--font-size-fluid-heading-900) * var(--line-height-ratio-heading));
  --font-size-fluid-display-1000: var(--font-size-72);
  --line-height-fluid-display-1000: calc(var(--font-size-fluid-display-1000) * var(--line-height-ratio-heading));
  --font-size-fluid-display-1100: var(--font-size-100);
  --line-height-fluid-display-1100: calc(var(--font-size-fluid-display-1100) * var(--line-height-ratio-heading));
`;
var smallBreakpointFontVariables = css`
  --font-size-fluid-heading-500: var(--font-size-22);
  --font-size-fluid-heading-700: var(--font-size-32);
  --font-size-fluid-heading-800: var(--font-size-34);
  --font-size-fluid-heading-900: var(--font-size-44);
  --font-size-fluid-display-1000: var(--font-size-56);
  --font-size-fluid-display-1100: var(--font-size-56);
`;
var formFieldStyles = css`
  --color-surface-input: var(--color-surface-neutral);
  --color-surface-input-disabled: var(--color-surface-neutral-disabled);
  --color-text-input: var(--blue-400);
  --color-text-input-placeholder: var(--color-text-placeholder);
  --color-border-input: var(--color-border);
  --color-border-input-focus: var(--color-border-focused);
  --font-size-input: var(--font-size-16);
  --line-height-input: var(--line-height-28);
  --padding-input-y: var(--grid-12);
  --padding-input-x: var(--grid-16);
`;
var gridSpacingVariables = css`
  --grid-2: 0.125rem; // 2px
  --grid-4: 0.25rem; // 4px
  --grid-8: 0.5rem; // 8px
  --grid-12: 0.75rem; // 12px
  --grid-16: 1rem; // 16px
  --grid-24: 1.5rem; // 24px
  --grid-32: 2rem; // 32px
  --grid-40: 2.5rem; // 40px
  --grid-48: 3rem; // 48px
  --grid-72: 4.5rem; // 72px
  --grid-96: 6rem; // 96px
  --grid-128: 8rem; // 128px
  --grid-144: 9rem; // 144px
`;
var radiusVariables = css`
  --radius-4: 4px;
  --radius-8: 8px;
  --radius-12: 12px;
  --radius-16: 16px;
  --radius-24: 24px;
  --radius-full: 1000px;
`;

// src/styles/global.tsx
var GlobalStyles = createGlobalStyle`
  * {
    box-sizing: border-box;
  }

  *, html, body {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    -webkit-text-size-adjust: 100%;

    ${fontFaceDeclarations}
    ${fontVariables}
    ${colorVariables}
    ${gridSpacingVariables}
    ${radiusVariables}
    ${buttonStyles}
  }

`;

export { BREAKPOINTS, DSAccordion, DSBadge, DSButton, DSDatePicker, DSDivider, DSFlex, DSIcon, DSIconGrid, DSModal, DSRadioButtonGroup, DSRangeSlider, DSSlider, DSSpacer, DSSurface, DSTabs, DSText, DSTextAndIcon, DSTextEm, DSTextStrong, GlobalStyles, buttonStyles, colorVariables, fontFaceDeclarations, fontVariables, formFieldStyles, gridSpacingVariables, radiusVariables, smallBreakpointFontVariables };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.mjs.map